import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { translateStepName } from '@/lib/stepNames';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FolderKanban, Plus, Calendar, Clock, CheckCircle2, Trash2, Edit, ChevronDown, ChevronUp, GripVertical, Pencil, Share2, Search, Archive, RotateCcw, Wrench, Droplets, FileImage, FileText, FileCheck, Camera, ImageIcon, X, Phone, MapPin, Navigation, Building2, Package, AlertTriangle, Users, History, Truck, Pause, Play, Download } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRESS_OPTIONS = [0, 25, 50, 75, 100];

import { markModuleSeen } from '@/lib/moduleBadges';

// Enregistre une photo sur le telephone ou l'ordinateur. Sur mobile on passe par
// le partage natif (la photo atterrit dans la galerie ou dans l'application
// choisie) ; ailleurs, telechargement classique. Demande de Pavel 19/09/2026 :
// pouvoir recuperer les photos jointes aux avancements d'etapes.
async function telechargerPhoto(dataUri, nomFichier) {
  try {
    const blob = await (await fetch(dataUri)).blob();
    const file = new File([blob], nomFichier, { type: blob.type || 'image/jpeg' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file] });
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nomFichier;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error('Telechargement de la photo impossible:', e);
  }
}

// Un seul deplacement de file a la fois (double appui sur mobile).
let fabMoving = false;

export default function ProjectsPage() {
  // Ouvrir le module efface sa pastille de notifications
  useEffect(() => { markModuleSeen(['project']); }, []);
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isManager = user?.role === 'manager';
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [expandedProject, setExpandedProject] = useState(null);
  const [showAddStep, setShowAddStep] = useState(false);
  const [addStepProjectId, setAddStepProjectId] = useState(null);
  const [newStepName, setNewStepName] = useState('');
  const [showRenameStep, setShowRenameStep] = useState(false);
  const [renameStepData, setRenameStepData] = useState({ projectId: null, stepId: null, currentName: '' });
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareProject, setShareProject] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [progressFilter, setProgressFilter] = useState('all'); // all | rouge | jaune | vert (avancement)
  const [searchQuery, setSearchQuery] = useState('');
  const [filterYear, setFilterYear] = useState('all');
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiveProjectId, setArchiveProjectId] = useState(null);
  const [archiveProjectName, setArchiveProjectName] = useState('');
  const [showDailyPhotos, setShowDailyPhotos] = useState(false);
  const [dailyPhotosProject, setDailyPhotosProject] = useState(null);
  const [dailyPhotos, setDailyPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [archivesExpanded, setArchivesExpanded] = useState(false);
  const [laborStats, setLaborStats] = useState({});
  const [friseTip, setFriseTip] = useState(null); // Pavel 24/09/2026 : bulle date au clic/toucher sur la frise
  const [workedDays, setWorkedDays] = useState({}); // pid -> { dates: [...], names: {date: [...]}}
  const [planStart, setPlanStart] = useState(() => new Date().toISOString().split('T')[0]); // début du planning de fabrication
  const [showCA, setShowCA] = useState(false); // CA prévisionnel des chantiers de la file (Pavel 28/09/2026), masqué par défaut
  const [filterCompany, setFilterCompany] = useState('all');
  // Gabarit d'étapes à la création (Pavel 17/09/2026) : taille exacte du bassin + options.
  // Durées jours ouvrés : plaques PVC 2 j (habillage, Pavel 25/09/2026) ; accessoires 1 j / 2 j ;
  // étanchéité 2 j (4,30) / 3 j (5 et 6) / 4 j (grand) ; local technique 3 j partout.
  // Bases : 4,30 m = 22 j ; 5 et 6 m = 23 j ; grand 8 à 12 m = 33 j.
  const [stepSize, setStepSize] = useState('6'); // '430' | '5' | '6' | 'grand' | 'vide'
  const [stepOptions, setStepOptions] = useState({ volet: false, ncc: false, habillage: false, terrasse: false, plage: false });
  // Partie du container (Pavel 25/09/2026) : "haute" (habillage PVC 2 j) ou "basse"
  // (1,5 j). Plage immergée +0,5 j et volet roulant +0,5 j sur l'habillage.
  const [containerPart, setContainerPart] = useState('haute');
  // Duree de l'habillage PVC (petit bassin) selon la partie + options.
  const habillagePetit = (containerPart === 'basse' ? 1.5 : 2)
    + (stepOptions.plage ? 0.5 : 0) + (stepOptions.volet ? 0.5 : 0);
  // Base = 4,30 m 22 j / 5-6 m 23 j / grand 33 j (l'habillage y compte 2 j) ;
  // on remplace ces 2 j par la valeur reelle pour le petit bassin.
  const gabaritBase = stepSize === 'vide' ? 0 : (stepSize === 'grand' ? 33 : stepSize === '430' ? 22 : 23);
  const gabaritTotal = stepSize === 'vide' ? 0
    : Math.ceil(gabaritBase - (stepSize === 'grand' ? 0 : 2) + (stepSize === 'grand' ? 0 : habillagePetit)
      + (stepOptions.volet ? 1 : 0) + (stepOptions.ncc ? 1 : 0)
      + (stepOptions.habillage ? 2 : 0) + (stepOptions.terrasse ? 3 : 0));
  const [form, setForm] = useState({
    name: '',
    client_address: '',
    client_phone: '',
    client_email: '',
    description: '',
    estimated_days: 30,
    pool_model: '',
    is_internal: false,
    sale_price: '',
    material_costs: [],
    budget_cost: '',
    start_date: new Date().toISOString().split('T')[0],
    target_date: '',
    sketch_photo: '',
    delivery_note_photo: '',
    cover_photo: '',
    container_part: 'haute',
    plage_immergee: false,
    volet_roulant: false,
    company: user?.company || 'La Piscine Container'
  });

  // Available companies for manager
  const COMPANIES = [
    { id: 'La Piscine Container', name: 'La Piscine Container', icon: '🏊' },
    { id: 'Eco Bati Construction', name: 'Eco Bati Construction', icon: '🏗️' },
    { id: 'Toutes', name: { fr: 'Les 2 sociétés (LPC + EBC)', ru: 'Обе компании (LPC + EBC)', md: 'Ambele companii (LPC + EBC)', uk: 'Обидві компанії (LPC + EBC)' }[language] || 'Les 2 sociétés (LPC + EBC)', icon: '🏢' }
  ];

  const locale = language === 'ru' ? 'ru-RU' : language === 'md' ? 'ro-RO' : 'fr-FR';
  
  // Generate year options (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLaborStats = async () => {
    try {
      const response = await axios.get(`${API}/projects/labor-stats`);
      setLaborStats(response.data || {});
    } catch (error) { /* manager only, silently absent for employees */ }
  };

  const fetchWorkedDays = async () => {
    try {
      // Cache-bust : la frise doit refléter les pointages du jour tout de suite
      // (Pavel 25/09/2026), jamais une réponse HTTP mise en cache par le navigateur.
      const response = await axios.get(`${API}/projects/worked-days`, {
        params: { _: Date.now() }, headers: { 'Cache-Control': 'no-cache' } });
      setWorkedDays(response.data || {});
    } catch (error) { /* frise sans blocs de pointage si indisponible */ }
  };

  useEffect(() => {
    fetchProjects();
    fetchWorkedDays();
    if (user?.role === 'manager') fetchLaborStats();

  }, []);

  // Rafraîchissement au retour sur l'onglet (Pavel 25/09/2026) : quand un salarié
  // vient de pointer, le manager revoit la frise à jour sans recharger la page.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchProjects();
        fetchWorkedDays();
        if (user?.role === 'manager') fetchLaborStats();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Handle document upload (sketch or delivery note)
  const handleDocumentUpload = (e, fieldName) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      const msg = { fr: 'Sélectionnez une image', ru: 'Выберите изображение', md: 'Selectați o imagine', uk: 'Виберіть зображення' };
      toast.error(msg[language] || msg.fr);
      return;
    }
    
    // Compress image before storing
    const img = new window.Image();
    const reader = new FileReader();
    reader.onloadend = () => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxW = 1200;
        const scale = Math.min(1, maxW / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const compressed = canvas.toDataURL('image/jpeg', 0.7);
        setForm(prev => ({ ...prev, [fieldName]: compressed }));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  // Helper for translations
  const txt = (fr, ru, md, uk) => ({ fr, ru, md, uk }[language] || fr);

  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  
  // Close preview on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && previewPhoto) {
        setPreviewPhoto(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [previewPhoto]);
  
  const closePreview = () => {
    console.log('Closing preview...');
    setPreviewPhoto(null);
    // Reopen the photos dialog
    setShowDailyPhotos(true);
  };

  // Daily photos functions
  const openDailyPhotos = async (project) => {
    setDailyPhotosProject(project);
    setShowDailyPhotos(true);
    setLoadingPhotos(true);
    
    try {
      const response = await axios.get(`${API}/projects/${project.id}/daily-photos`);
      setDailyPhotos(response.data);
    } catch (error) {
      console.error('Failed to fetch daily photos:', error);
      toast.error(t('error'));
    } finally {
      setLoadingPhotos(false);
    }
  };

  const handleAddMultiplePhotos = async (files) => {
    if (!dailyPhotosProject || !files.length) return;
    
    setUploadingPhotos(true);
    const uploadedPhotos = [];
    
    // Compress image function - reduces size by ~25%
    const compressImage = (file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new window.Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            // Reduce dimensions by 25% (keep 75% of original size)
            const scale = 0.75;
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            // Compress with 70% quality JPEG
            const compressedData = canvas.toDataURL('image/jpeg', 0.7);
            resolve(compressedData);
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      });
    };
    
    try {
      for (const file of files) {
        const photoData = await compressImage(file);
        
        const response = await axios.post(`${API}/projects/${dailyPhotosProject.id}/daily-photos`, {
          project_id: dailyPhotosProject.id,
          photo_data: photoData,
          description: ''
        });
        uploadedPhotos.push(response.data);
      }
      
      setDailyPhotos([...uploadedPhotos, ...dailyPhotos]);
      toast.success(
        files.length === 1 
          ? txt('Photo ajoutée', 'Фото добавлено', 'Foto adăugată', 'Фото додано')
          : txt(`${files.length} photos ajoutées`, `${files.length} фото добавлено`, `${files.length} foto adăugate`, `${files.length} фото додано`)
      );
    } catch (error) {
      console.error('Failed to add photos:', error);
      toast.error(t('error'));
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleAddDailyPhoto = async (photoData, description = '') => {
    if (!dailyPhotosProject) return;
    
    try {
      const response = await axios.post(`${API}/projects/${dailyPhotosProject.id}/daily-photos`, {
        project_id: dailyPhotosProject.id,
        photo_data: photoData,
        description
      });
      setDailyPhotos([response.data, ...dailyPhotos]);
      toast.success(txt('Photo ajoutée', 'Фото добавлено', 'Foto adăugată', 'Фото додано'));
    } catch (error) {
      console.error('Failed to add daily photo:', error);
      toast.error(t('error'));
    }
  };

  const handleDeleteDailyPhoto = async (photoId) => {
    if (!dailyPhotosProject) return;
    
    const confirmMsg = txt(
      'Voulez-vous vraiment supprimer cette photo ?',
      'Вы уверены, что хотите удалить это фото?',
      'Sigur doriți să ștergeți această foto?',
      'Ви впевнені, що хочете видалити це фото?'
    );
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API}/projects/${dailyPhotosProject.id}/daily-photos/${photoId}`);
      setDailyPhotos(dailyPhotos.filter(p => p.id !== photoId));
      toast.success(txt('Photo supprimée', 'Фото удалено', 'Foto ștearsă', 'Фото видалено'));
    } catch (error) {
      console.error('Failed to delete daily photo:', error);
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  // Handle cover photo change for a project
  const handleCoverPhotoChange = async (projectId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error(txt('Sélectionnez une image', 'Выберите изображение', 'Selectați o imagine', 'Виберіть зображення'));
      return;
    }
    
    // Compress image
    const reader = new FileReader();
    reader.onloadend = async () => {
      const img = new window.Image();
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, 400 / Math.max(img.width, img.height)); // Max 400px
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const compressedData = canvas.toDataURL('image/jpeg', 0.8);
        
        try {
          const response = await axios.put(`${API}/projects/${projectId}/cover-photo`, {
            cover_photo: compressedData
          });
          setProjects(projects.map(p => p.id === projectId ? response.data : p));
          toast.success(txt('Photo mise à jour', 'Фото обновлено', 'Foto actualizată', 'Фото оновлено'));
        } catch (error) {
          console.error('Failed to update cover photo:', error);
          toast.error(t('error'));
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  // Handle cover photo delete for a project
  const handleCoverPhotoDelete = async (projectId) => {
    try {
      const response = await axios.delete(`${API}/projects/${projectId}/cover-photo`);
      setProjects(projects.map(p => p.id === projectId ? response.data : p));
      toast.success(txt('Photo supprimée', 'Фото удалено', 'Foto ștearsă', 'Фото видалено'));
    } catch (error) {
      console.error('Failed to delete cover photo:', error);
      toast.error(t('error'));
    }
  };

  // Filtre et recherche (les stocks permanents ne sont pas des chantiers)
  const filteredProjects = projects.filter(project => {
    // Les stocks (Depot, Sprinter, Master) ne vivent que dans Materiaux
    if (project.is_stock) {
      return false;
    }
    
    // Status filter
    if (filterStatus !== 'all' && project.status !== filterStatus) {
      return false;
    }
    
    // Year filter
    if (filterYear !== 'all') {
      const projectYear = new Date(project.start_date || project.created_at).getFullYear();
      if (projectYear !== parseInt(filterYear)) {
        return false;
      }
    }

    // Company filter
    if (filterCompany !== 'all') {
      const pc = project.company || 'La Piscine Container';
      if (pc !== filterCompany && pc !== 'Toutes') {
        return false;
      }
    }

    // Search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return project.name.toLowerCase().includes(query) || 
             (project.description && project.description.toLowerCase().includes(query));
    }
    
    return true;
  });

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error(t('error'));
      return;
    }
    if (!form.is_internal) {
      const em = (form.client_email || '').trim();
      if (!em || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) {
        toast.error(txt("L'email du client est obligatoire", 'Email клиента обязателен', "Emailul clientului este obligatoriu", "Email клієнта обов'язковий"));
        return;
      }
    }

    // Normalisation des nombres avant envoi (le backend attend des float, pas '').
    const clean = {
      ...form,
      sale_price: parseFloat(form.sale_price) || 0,
      budget_cost: parseFloat(form.budget_cost) || 0,
      material_costs: (form.material_costs || [])
        .filter(m => (m.label || '').trim())
        .map(m => ({ label: m.label.trim(), amount: parseFloat(m.amount) || 0 })),
    };
    try {
      if (editProject) {
        // Update project with all data including photos
        const response = await axios.put(`${API}/projects/${editProject.id}`, clean);
        setProjects(projects.map(p => p.id === editProject.id ? response.data : p));
        toast.success(t('projectUpdated'));
      } else {
        // Step template (LPC pools): standard, standard + volet immergé, or empty
        // Gabarits piscine container en jours ouvrés (Pavel 17/09/2026) : taille exacte.
        // Étanchéité (membrane armée) : 2 j pour 4,30 m, 3 j pour 5 et 6 m, 4 j pour 8 à 12 m.
        // Plaques PVC et accessoires : 1 j (petit) / 2 j (grand). Local technique : 3 j partout.
        const grand = stepSize === 'grand';
        const membraneJours = grand ? 4 : (stepSize === '430' ? 2 : 3);
        const STEPS_PETIT = [
          ['Découpe du container', 1], ['Structure de la piscine', 6],
          ['Préparation du container avant peinture', 1], ['Peinture', 2],
          ['Pose des plaques PVC', habillagePetit], ['Pose des accessoires', 1],
          ['Préparation avant la membrane', 1], ['Pose de la membrane armée', membraneJours],
          ['Pose des margelles', 2],
        ];
        const STEPS_GRAND = [
          ['Découpe du container', 1], ['Structure de la piscine', 11],
          ['Préparation du container avant peinture', 2], ['Peinture', 2],
          ['Pose des plaques PVC et habillage', 2], ['Pose des accessoires', 2],
          ['Préparation avant la membrane', 2], ['Pose de la membrane armée et feutrine', membraneJours],
          ['Pose des margelles', 2],
        ];
        // Local technique : 3 j quelle que soit la taille (Pavel 17/09/2026).
        const TECH_STEPS = [['Machinerie et local technique', 3], ['Pose de la pompe à chaleur', 1]];
        const LAST_STEP = ['Test, mise en eau et bâchage', 1];
        let payload = { ...clean };
        const isLpcPool = (form.company || 'La Piscine Container') !== 'Eco Bati Construction';
        if (isLpcPool) {
          let list = [];
          if (stepSize !== 'vide') {
            list = [...(grand ? STEPS_GRAND : STEPS_PETIT)];
            if (stepOptions.habillage) list.push(['Habillage bois des margelles', 2]);
            if (stepOptions.terrasse) list.push(['Terrasse coulissante', 3]);
            list.push(...TECH_STEPS);
            if (stepOptions.volet) list.push(['Pose du volet roulant', 1]);
            if (stepOptions.ncc) list.push(['Nage à contre-courant', 1]);
            list.push(LAST_STEP);
          }
          payload.steps = list.map(([n, d]) => ({ id: crypto.randomUUID(), name: n, progress: 0, days: d }));
          if (list.length && !form.target_date) {
            payload.estimated_days = Math.ceil(list.reduce((a, [, d]) => a + d, 0));
          }
          payload.container_part = containerPart;
          payload.plage_immergee = !!stepOptions.plage;
          payload.volet_roulant = !!stepOptions.volet;
        }
        const response = await axios.post(`${API}/projects`, payload);
        setProjects([response.data, ...projects]);
        toast.success(t('projectCreated'));
      }
      closeDialog();
    } catch (error) {
      console.error('Project submit error:', error);
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  // Attach a photo to a fabrication step (compressed client-side)
  const handleAddStepPhoto = (projectId, stepId, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    const img = new window.Image();
    const reader = new FileReader();
    reader.onloadend = () => {
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        const maxW = 1000;
        const scale = Math.min(1, maxW / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const compressed = canvas.toDataURL('image/jpeg', 0.6);
        try {
          const response = await axios.post(`${API}/projects/${projectId}/steps/${stepId}/photos`, { photo: compressed });
          setProjects(prev => prev.map(p => p.id === projectId ? response.data : p));
          toast.success(txt('Photo ajoutée', 'Фото добавлено', 'Foto adăugată', 'Фото додано'));
        } catch (error) {
          toast.error(error.response?.data?.detail || t('error'));
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteStepPhoto = async (projectId, stepId, photoIndex) => {
    try {
      const response = await axios.delete(`${API}/projects/${projectId}/steps/${stepId}/photos/${photoIndex}`);
      setProjects(prev => prev.map(p => p.id === projectId ? response.data : p));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleStepProgress = async (projectId, stepId, progress) => {
    try {
      const response = await axios.put(`${API}/projects/${projectId}/step`, {
        step_id: stepId,
        progress: progress
      });
      setProjects(projects.map(p => p.id === projectId ? response.data : p));
      
      if (response.data.overall_progress === 100) {
        toast.success(t('projectCompleted'));
      }
    } catch (error) {
      toast.error(t('error'));
    }
  };

  // Durée d'une étape modifiable par le manager (Pavel 25/09/2026 : durées données
  // au fur et à mesure, pour chaque étape). Demi-journées acceptées (ex. 1,5).
  const handleEditDays = async (projectId, step) => {
    const cur = step.days != null ? String(step.days).replace('.', ',') : '';
    const saisie = window.prompt(
      `Durée de « ${step.name} » en jours ouvrés (0,5 possible) :`, cur);
    if (saisie == null) return;
    const days = parseFloat(String(saisie).replace(',', '.').trim());
    if (isNaN(days) || days < 0) { toast.error('Durée invalide'); return; }
    try {
      const response = await axios.put(`${API}/projects/${projectId}/step-days`, {
        step_id: step.id, days });
      setProjects(projects.map(p => p.id === projectId ? response.data : p));
      toast.success('Durée mise à jour');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  // Partie du container (haute/basse) + plage immergée + volet roulant : cale
  // l'habillage PVC et recalcule les délais (Pavel 25/09/2026).
  const handleContainerChange = async (projectId, part, plage, volet) => {
    try {
      const response = await axios.put(`${API}/projects/${projectId}/container`, {
        container_part: part, plage_immergee: !!plage, volet_roulant: !!volet });
      setProjects(projects.map(p => p.id === projectId ? response.data : p));
      toast.success(part === 'basse' ? 'Partie basse : délais ajustés' : 'Partie haute : délais ajustés');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  // Déplacer un chantier dans la file de fabrication (Pavel 25/09/2026) : glisser
  // un chantier avant/après un autre recalcule les dates prévisionnelles de tous.
  // Ordre de la file de fabrication (Pavel 28/09/2026) : `queue_order` s'applique a TOUS les
  // chantiers de la file (en cours + en attente). Sans ordre explicite : en cours d'abord (le
  // plus avance en tete), puis en attente par date de creation ; un chantier ajoute plus tard
  // sans ordre se place en fin de file. Les internes (depot, atelier) et les 100 % sont hors file.
  const sortFabQueue = (list) => {
    const eligible = list.filter(p =>
      (p.status === 'in_progress' && !p.is_internal && (p.overall_progress || 0) < 100) || p.status === 'en_attente');
    const cours = eligible.filter(p => p.status === 'in_progress')
      .sort((a, b) => ((b.overall_progress || 0) - (a.overall_progress || 0)) || (new Date(a.start_date || a.created_at) - new Date(b.start_date || b.created_at)));
    const att = eligible.filter(p => p.status !== 'in_progress')
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const rank = new Map();
    cours.forEach((p, i) => rank.set(p.id, -1e6 + i));
    att.forEach((p, i) => rank.set(p.id, 1e9 + i));
    const key = (p) => (p.queue_order != null ? p.queue_order : rank.get(p.id));
    return [...eligible].sort((a, b) => key(a) - key(b));
  };

  // Monter / descendre un chantier dans la file (tous les chantiers, en cours compris) :
  // on reecrit l'ordre 0..n-1 de toute la file pour qu'il soit explicite partout.
  const moveQueue = async (projId, dir) => {
    if (fabMoving) return;
    const list = sortFabQueue(projects);
    const i = list.findIndex(p => p.id === projId);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return;
    const reordered = [...list];
    [reordered[i], reordered[j]] = [reordered[j], reordered[i]];
    const orderById = Object.fromEntries(reordered.map((p, idx) => [p.id, idx]));
    // Affichage immediat du nouvel ordre (lignes ET frise), avant la reponse du serveur.
    setProjects(prev => prev.map(p => (orderById[p.id] != null ? { ...p, queue_order: orderById[p.id] } : p)));
    fabMoving = true;
    try {
      await Promise.all(reordered.map((p, idx) => axios.put(`${API}/projects/${p.id}/queue-order?order=${idx}`)));
      await fetchProjects();
    } catch (e) {
      toast.error(t('error'));
      fetchProjects();
    } finally {
      fabMoving = false;
    }
  };

  const handleAddStep = async (projectId) => {
    setAddStepProjectId(projectId);
    setNewStepName('');
    setShowAddStep(true);
  };

  const submitAddStep = async () => {
    if (!newStepName.trim() || !addStepProjectId) return;
    
    try {
      const response = await axios.post(`${API}/projects/${addStepProjectId}/steps?step_name=${encodeURIComponent(newStepName.trim())}`);
      setProjects(projects.map(p => p.id === addStepProjectId ? response.data : p));
      toast.success(t('success'));
      setShowAddStep(false);
      setNewStepName('');
      setAddStepProjectId(null);
    } catch (error) {
      console.error('Add step error:', error);
      toast.error(t('error'));
    }
  };

  const handleRenameStep = (projectId, stepId, currentName) => {
    setRenameStepData({ projectId, stepId, currentName });
    setShowRenameStep(true);
  };

  const submitRenameStep = async () => {
    const { projectId, stepId, currentName } = renameStepData;
    if (!currentName.trim()) return;
    
    try {
      const response = await axios.put(`${API}/projects/${projectId}/steps/${stepId}/rename?new_name=${encodeURIComponent(currentName.trim())}`);
      setProjects(projects.map(p => p.id === projectId ? response.data : p));
      toast.success(t('success'));
      setShowRenameStep(false);
      setRenameStepData({ projectId: null, stepId: null, currentName: '' });
    } catch (error) {
      console.error('Rename step error:', error);
      toast.error(t('error'));
    }
  };

  const handleReorderSteps = async (projectId, newStepsOrder) => {
    try {
      const response = await axios.put(`${API}/projects/${projectId}/steps/reorder`, {
        step_ids: newStepsOrder.map(s => s.id)
      });
      setProjects(projects.map(p => p.id === projectId ? response.data : p));
    } catch (error) {
      console.error('Reorder steps error:', error);
      toast.error(t('error'));
    }
  };

  const handleDeleteStep = async (projectId, stepId) => {
    const confirmMsg = txt(
      'Voulez-vous vraiment supprimer cette étape ?',
      'Вы уверены, что хотите удалить этот этап?',
      'Sigur doriți să ștergeți această etapă?',
      'Ви впевнені, що хочете видалити цей етап?'
    );
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const response = await axios.delete(`${API}/projects/${projectId}/steps/${stepId}`);
      setProjects(projects.map(p => p.id === projectId ? response.data : p));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  // Bascule stand-by <-> en cours (Pavel 17/09/2026 : chantier Chevallier bloque par la mairie/ABF)
  const handleToggleStandby = async (project) => {
    const next = project.status === 'standby' ? 'in_progress' : 'standby';
    try {
      await axios.put(`${API}/projects/${project.id}/status?new_status=${next}`);
      toast.success(next === 'standby'
        ? txt('Chantier mis en stand-by', 'Стройка на паузе', 'Șantier în așteptare', 'Об\'єкт на паузі')
        : txt('Chantier repris', 'Стройка возобновлена', 'Șantier reluat', 'Об\'єкт відновлено'));
      await fetchProjects();
      // fetchProjects rend la liste ALLEGEE : le chantier ouvert perdrait ses
      // photos. On le recharge de force.
      setChantiersCharges({});
      if (expandedProject) chargerChantierComplet(expandedProject, true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  // Clic sur un chantier de la frise : on déplie sa carte dans la liste et on y descend
  // (la carte porte déjà data-testid="project-<id>", pas de page dédiée par chantier).
  const goToProject = (projectId) => {
    setProgressFilter('all');
    setFilterStatus('all');
    setExpandedProject(projectId);
    // Les photos d'etapes et le croquis ne sont plus dans la liste (allegement
    // du 19/09) : sans cet appel, un chantier ouvert depuis la frise ou depuis
    // le tableau de bord s'affichait SANS ses photos (signalement Pavel 19/09,
    // chantier Sayou). toggleExpand le fait deja pour un clic sur la carte.
    chargerChantierComplet(projectId);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="project-${projectId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  };

  const handleDelete = async (projectId, projectName) => {
    // Show custom confirmation dialog instead of window.confirm
    setArchiveProjectId(projectId);
    setArchiveProjectName(projectName || 'ce chantier');
    setShowArchiveConfirm(true);
  };

  const confirmArchive = async () => {
    if (!archiveProjectId) return;
    
    try {
      await axios.delete(`${API}/projects/${archiveProjectId}`);
      // Update local state with archived status
      setProjects(projects.map(p => p.id === archiveProjectId ? { 
        ...p, 
        status: 'archived',
        archived_at: new Date().toISOString()
      } : p));
      toast.success(txt('Chantier archivé', 'Проект архивирован', 'Proiect arhivat', 'Об\'єкт архівовано'));
    } catch (error) {
      console.error('Archive error:', error);
      toast.error(t('error'));
    } finally {
      setShowArchiveConfirm(false);
      setArchiveProjectId(null);
      setArchiveProjectName('');
    }
  };

  const handleRestore = async (projectId) => {
    const confirmMsg = txt(
      'Voulez-vous vraiment restaurer ce chantier ?',
      'Вы уверены, что хотите восстановить этот проект?',
      'Sigur doriți să restaurați acest proiect?',
      'Ви впевнені, що хочете відновити цей об\'єкт?'
    );
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const response = await axios.put(`${API}/projects/${projectId}/restore`);
      setProjects(projects.map(p => p.id === projectId ? response.data : p));
      toast.success(txt('Chantier restauré', 'Проект восстановлен', 'Proiect restaurat', 'Об\'єкт відновлено'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handlePermanentDelete = async (projectId, projectName) => {
    const confirmMsg = txt(
      `Êtes-vous sûr de vouloir supprimer définitivement "${projectName}" ? Cette action est irréversible.`,
      `Вы уверены, что хотите окончательно удалить "${projectName}"? Это действие необратимо.`,
      `Sigur doriți să ștergeți permanent "${projectName}"? Această acțiune nu poate fi anulată.`,
      `Ви впевнені, що хочете остаточно видалити "${projectName}"? Ця дія незворотна.`
    );
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}/permanent`);
      setProjects(projects.filter(p => p.id !== projectId));
      toast.success(txt('Chantier supprimé définitivement', 'Проект удален', 'Proiect șters', 'Об\'єкт видалено'));
    } catch (error) {
      console.error('Delete error:', error);
      const errorMsg = error.response?.data?.detail || t('error');
      toast.error(errorMsg);
    }
  };

  const handleShare = (project) => {
    setShareProject(project);
    setShowShareDialog(true);
  };

  const generateShareText = (project) => {
    const progress = project.overall_progress || 0;
    const steps = project.steps || [];
    const completedSteps = steps.filter(s => s.progress === 100).length;
    
    let text = `📋 *${project.name}*\n\n`;
    text += `📊 Progression: ${progress}%\n`;
    text += `✅ Étapes: ${completedSteps}/${steps.length} terminées\n`;
    text += `📅 Début: ${new Date(project.start_date).toLocaleDateString(locale)}\n`;
    text += `⏱ Durée estimée: ${project.estimated_days} jours\n\n`;
    
    if (steps.length > 0) {
      text += `*Détail des étapes:*\n`;
      steps.forEach((step, idx) => {
        const icon = step.progress === 100 ? '✅' : step.progress >= 50 ? '🔄' : '⏳';
        text += `${icon} ${step.name}: ${step.progress}%\n`;
      });
    }
    
    return text;
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(generateShareText(shareProject));
    window.open(`https://wa.me/?text=${text}`, '_blank');
    setShowShareDialog(false);
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Chantier: ${shareProject.name}`);
    const body = encodeURIComponent(generateShareText(shareProject).replace(/\*/g, ''));
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    setShowShareDialog(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generateShareText(shareProject).replace(/\*/g, ''));
    toast.success(txt('Copié !', 'Скопировано', 'Copiat', 'Скопійовано'));
    setShowShareDialog(false);
  };

  const openEdit = async (chantierDeLaListe) => {
    // La liste ne porte plus le croquis ni la photo de piscine : on recharge le
    // chantier complet AVANT de remplir le formulaire, sinon un enregistrement
    // les effacerait.
    const project = (await chargerChantierComplet(chantierDeLaListe.id)) || chantierDeLaListe;
    setEditProject(project);
    setForm({
      name: project.name,
      client_address: project.client_address || '',
      client_phone: project.client_phone || '',
      client_email: project.client_email || '',
      pool_photo: project.pool_photo || '',
      description: project.description || '',
      estimated_days: project.estimated_days,
      pool_model: project.pool_model || '',
      is_internal: !!project.is_internal,
      sale_price: project.sale_price || '',
      material_costs: Array.isArray(project.material_costs) ? project.material_costs.map(m => ({ ...m })) : [],
      budget_cost: project.budget_cost || '',
      start_date: project.start_date,
      target_date: project.target_date || '',
      sketch_photo: project.sketch_photo || '',
      delivery_note_photo: project.delivery_note_photo || '',
      cover_photo: project.cover_photo || '',
      container_part: project.container_part || 'haute',
      plage_immergee: !!project.plage_immergee,
      volet_roulant: !!project.volet_roulant
    });
    setShowAdd(true);
  };

  const closeDialog = () => {
    setShowAdd(false);
    setEditProject(null);
    setStepSize('6'); setStepOptions({ volet: false, ncc: false, habillage: false, terrasse: false });
    setForm({
      name: '',
      client_address: '',
      client_phone: '',
      client_email: '',
      pool_photo: '',
      description: '',
      estimated_days: 30,
      pool_model: '',
      is_internal: false,
      sale_price: '',
      material_costs: [],
      budget_cost: '',
      start_date: new Date().toISOString().split('T')[0],
      target_date: '',
      sketch_photo: '',
      delivery_note_photo: '',
      cover_photo: '',
      container_part: 'haute',
      plage_immergee: false,
      volet_roulant: false
    });
  };

  // Chantiers dont les photos ont deja ete chargees (voir chargerChantierComplet).
  const [chantiersCharges, setChantiersCharges] = useState({});

  // La liste des chantiers ne transporte plus les photos d'etapes ni le croquis
  // (27,5 Mo avant, environ 96 s sur une 4G faible : la page restait vide sur un
  // chantier mal couvert). On va les chercher chantier par chantier, a
  // l'ouverture, et une seule fois.
  const chargerChantierComplet = async (projectId, force = false) => {
    if (!force && chantiersCharges[projectId]) return projects.find(p => p.id === projectId);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/full`);
      const complet = response.data;
      setProjects(prev => prev.map(p => (p.id === projectId ? { ...p, ...complet } : p)));
      setChantiersCharges(prev => ({ ...prev, [projectId]: true }));
      return complet;
    } catch (error) {
      console.error('Failed to fetch full project:', error);
    }
  };

  // Ouvre un document (croquis, bon de livraison) d'un chantier : l'image n'est
  // plus dans la liste, on la charge au clic.
  const ouvrirDocument = async (project, champ) => {
    if (project[champ]) { setPreviewDoc(project[champ]); return; }
    const complet = await chargerChantierComplet(project.id);
    if (complet && complet[champ]) setPreviewDoc(complet[champ]);
  };

  const toggleExpand = (projectId) => {
    const ouvre = expandedProject !== projectId;
    setExpandedProject(ouvre ? projectId : null);
    if (ouvre) chargerChantierComplet(projectId);
  };

  // Total counts (from ALL projects, not filtered) for stat cards
  const totalActiveProjects = projects.filter(p => p.status === 'in_progress').length;
  const totalSavProjects = projects.filter(p => p.status === 'sav').length;
  const totalMaintenanceProjects = projects.filter(p => p.status === 'maintenance').length;
  const totalStandbyProjects = projects.filter(p => p.status === 'standby').length;
  const totalArchivedProjects = projects.filter(p => p.status === 'archived' || p.status === 'completed').length;

  // Avancement -> feu tricolore (ordre Pavel 17/09/2026) :
  // rouge < 33 %, jaune 33-66 %, vert > 66 %.
  const progressBucket = (p) => {
    const pr = p.overall_progress || 0;
    if (pr < 33) return 'rouge';
    if (pr <= 66) return 'jaune';
    return 'vert';
  };

  // Tableau de bord chantiers : basé sur TOUS les chantiers en cours,
  // indépendamment des filtres du bas, pour une vue de pilotage stable.
  const chantiersEnCours = projects.filter(p => p.status === 'in_progress');
  // Frise : une ligne par chantier en cours, du début à la fin prévue.
  // Fin prévue = « Date de livraison prévue » si renseignée, sinon début + jours estimés.
  const DAY_MS = 86400000;
  // Jours fériés français (Pavel 17/09/2026) : le planning saute les week-ends ET les
  // jours fériés. Pâques par l'algorithme de Butcher, cache par année.
  const _isoLocal = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  const _holidayCache = {};
  const frHolidays = (y) => {
    if (_holidayCache[y]) return _holidayCache[y];
    const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
    const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
    const mo = Math.floor((h + l - 7 * m + 114) / 31), da = ((h + l - 7 * m + 114) % 31) + 1;
    const easter = new Date(y, mo - 1, da);
    const s = new Set([`${y}-01-01`, `${y}-05-01`, `${y}-05-08`, `${y}-07-14`, `${y}-08-15`, `${y}-11-01`, `${y}-11-11`, `${y}-12-25`]);
    [1, 39, 50].forEach(off => { const dt = new Date(easter); dt.setDate(dt.getDate() + off); s.add(_isoLocal(dt)); });
    _holidayCache[y] = s;
    return s;
  };
  const isWorkday = (dt) => { const w = dt.getDay(); return w !== 0 && w !== 6 && !frHolidays(dt.getFullYear()).has(_isoLocal(dt)); };
  // Jours ouvrés (lundi-vendredi hors jours fériés) : le gabarit d'étapes est en jours ouvrés.
  const addWD = (d, n) => { const r = new Date(d); let k = 0; while (k < n && k < 2000) { r.setDate(r.getDate() + 1); if (isWorkday(r)) k++; } return r; };
  const wdBetween = (a, b) => { let n = 0; const d = new Date(a); let g = 0; while (d.getTime() < b.getTime() && g < 4000) { d.setDate(d.getDate() + 1); g++; if (isWorkday(d)) n++; } return n; };
  const parseDay = (s) => {
    if (!s) return null;
    const d = new Date(String(s).slice(0, 10) + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
  };
  const todayDay = new Date(); todayDay.setHours(0, 0, 0, 0);
  // Frise (ordre Pavel 17/09/2026) : un chantier neuf arrive EN HAUT (0 %) et descend au fil
  // de l'avancement ; les chantiers internes (dépôt, atelier, is_internal) sont regroupés
  // TOUT EN BAS, sous une séparation.
  // File de fabrication = UNE seule source de dates pour le « Planning de fabrication » ET la
  // frise (Pavel 28/09/2026 : « je ne vois pas les memes dates »). Un seul poste de fabrication :
  // les chantiers se suivent, en cours avec leur temps RESTANT puis en attente avec leur duree
  // complete, en jours ouvres hors feries (memes helpers que la frise). Depart = `planStart`.
  const fabDurTotal = (p) => Math.max(1, Math.ceil((p.steps || []).reduce((a, s) => a + ((s.days || 0) > 0 ? s.days : 1), 0) || p.estimated_days || 1));
  const fabDurReste = (p) => Math.max(1, Math.ceil(fabDurTotal(p) * (100 - Math.min(100, p.overall_progress || 0)) / 100));
  const nextWD = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); let g = 0; while (!isWorkday(x) && g < 30) { x.setDate(x.getDate() + 1); g++; } return x; };
  const fabQueue = (() => {
    const list = sortFabQueue(projects);
    let cursor = nextWD(parseDay(planStart) || todayDay);
    return list.map((p, idx) => {
      const phase = p.status === 'in_progress' ? 'cours' : 'attente';
      const dur = phase === 'cours' ? fabDurReste(p) : fabDurTotal(p);
      const debut = nextWD(cursor);
      const fin = addWD(debut, dur - 1);
      cursor = addWD(fin, 1);
      return { p, phase, idx, dur, debut, fin };
    });
  })();
  const fabFinById = Object.fromEntries(fabQueue.map(r => [r.p.id, r.fin]));
  const fabIdxById = Object.fromEntries(fabQueue.map(r => [r.p.id, r.idx]));
  const fabById = Object.fromEntries(fabQueue.map(r => [r.p.id, r]));
  const friseRowsBase = chantiersEnCours.map(p => {
    const q = fabById[p.id];
    const pr = Math.min(100, Math.max(0, p.overall_progress || 0));
    // Demarrage (Pavel 28/09/2026) : reel des que le chantier a avance ; un chantier a 0 %
    // n'a pas encore demarre, quelle que soit la date saisie a sa creation : son demarrage
    // est sa place ESTIMEE dans la file de fabrication.
    const started = pr > 0 || !q;
    const start = started ? (parseDay(p.start_date) || parseDay(p.created_at) || new Date()) : q.debut;
    const startEst = !started;
    const target = parseDay(p.target_date); // date de livraison prevue (saisie), affichee a part
    const steps = Array.isArray(p.steps) ? p.steps : [];
    // Gabarit (Pavel 17/09/2026) : dès qu'une étape porte une durée, le plan est en jours
    // ouvrés (23 j de base) et l'« attendu » à ce jour en découle ; une étape ajoutée à la
    // main sans durée compte 1 j (même règle que le backend). Sinon, repli sur la durée
    // estimée en jours calendaires.
    const hasDays = steps.some(st => (st.days || 0) > 0);
    const totalWD = hasDays ? steps.reduce((a, st) => a + ((st.days || 0) > 0 ? st.days : 1), 0) : 0;
    const duree = Math.max(1, p.estimated_days || 30);
    // Fabrication terminee = demarrage + duree du gabarit (jours ouvres) : la date que le plan
    // donne a ce chantier. Pas encore demarre : sa fin dans la file, directement.
    const end = !started ? q.fin : (hasDays ? addWD(start, totalWD) : new Date(start.getTime() + duree * DAY_MS));
    // Date prévisionnelle : ce qui reste à faire (100 - %) au rythme du plan, compté à partir
    // d'aujourd'hui. Chaque jour sans avancement repousse la date d'un jour.
    const base = new Date(Math.max(start.getTime(), todayDay.getTime()));
    // Previsionnel = fin de ce chantier dans la file de fabrication (meme date que le
    // « Planning de fabrication », Pavel 28/09/2026) ; repli sur le calcul isole hors file.
    const fabFin = fabFinById[p.id];
    const forecast = fabFin ? fabFin : (pr >= 100 ? end
      : hasDays ? addWD(base, Math.ceil(totalWD * (100 - pr) / 100))
      : new Date(base.getTime() + Math.ceil(duree * (100 - pr) / 100) * DAY_MS));
    const retard = Math.round((forecast.getTime() - end.getTime()) / DAY_MS);
    const expected = hasDays
      ? (todayDay.getTime() < start.getTime() ? 0 : Math.min(100, Math.round(wdBetween(start, todayDay) / totalWD * 100)))
      : null;
    return { p, start, startEst, target, end, forecast, retard, interne: !!p.is_internal, hasDays, totalWD, expected };
  });
  // Chantiers EN ATTENTE (Pavel 28/09/2026) : ils font partie du planning, donc de la grille.
  // Leur ligne part de leur debut dans la file et finit a leur fin estimee ; pas de jours pointes.
  const friseRowsAttente = fabQueue.filter(r => r.phase === 'attente').map(r => {
    const p = r.p;
    const steps = Array.isArray(p.steps) ? p.steps : [];
    const hasDays = steps.some(st => (st.days || 0) > 0);
    const totalWD = hasDays ? steps.reduce((a, st) => a + ((st.days || 0) > 0 ? st.days : 1), 0) : 0;
    const target = parseDay(p.target_date);
    return { p, start: r.debut, startEst: true, target, end: r.fin, forecast: r.fin, retard: 0, interne: false, hasDays, totalWD, expected: null, attente: true };
  });
  // Ordre de la grille = ORDRE DE FABRICATION (numeros), internes tout en bas.
  const friseRows = [...friseRowsBase, ...friseRowsAttente].sort((a, b) =>
    (Number(a.interne) - Number(b.interne))
    || ((fabIdxById[a.p.id] ?? 1e9) - (fabIdxById[b.p.id] ?? 1e9))
    || ((a.p.overall_progress || 0) - (b.p.overall_progress || 0))
    || (b.start - a.start));

  // Filtered projects by status (for display)
  const activeProjects = filteredProjects
    .filter(p => p.status === 'in_progress')
    .filter(p => progressFilter === 'all' || progressBucket(p) === progressFilter)
    .sort((a, b) => Number(!!a.is_internal) - Number(!!b.is_internal)); // internes tout en bas
  const savProjects = filteredProjects.filter(p => p.status === 'sav');
  const maintenanceProjects = filteredProjects.filter(p => p.status === 'maintenance');
  const standbyProjects = filteredProjects.filter(p => p.status === 'standby');
  const archivedProjects = filteredProjects.filter(p => p.status === 'archived' || p.status === 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="projects-page" className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="page-header mb-0">{t('projectsTitle')}</h1>
        {/* Only manager can add projects */}
        {isManager && (
          <Button
            onClick={() => setShowAdd(true)}
            className="btn-primary"
            data-testid="add-project-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('addProject')}
          </Button>
        )}
      </div>

      {/* Tableau de bord chantiers : feu tricolore d'avancement + planning des livraisons */}
      <div className="card-base p-4 sm:p-5" data-testid="chantier-dashboard">
        <div className="flex items-center gap-2 mb-4">
          <Truck className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
          <h2 className="text-base sm:text-lg font-bold" style={{ color: 'var(--color-primary)' }}>
            {txt('Planning de fabrication', 'План производства', 'Planificarea fabricației', 'План виробництва')}
          </h2>
        </div>

      {/* Planning de fabrication (Pavel 28/09/2026) : UNE seule grille = la frise ci-dessous,
          avec numero de fabrication + fleches + dates sur chaque ligne. Ici : seulement la date
          de depart de la file et la derniere livraison estimee. */}
      {isManager && fabQueue.length > 0 && (() => {
        const fmt = (d) => d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: '2-digit' });
        const last = fabQueue[fabQueue.length - 1];
        const nCours = fabQueue.filter(r => r.phase === 'cours').length, nAttente = fabQueue.length - nCours;
        return (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 text-sm" data-testid="fabrication-plan">
            <span className="text-xs text-gray-500">{nCours} en cours + {nAttente} en attente · le n° 1 est le prochain à fabriquer</span>
            <span className="flex items-center gap-2">
              <label className="text-gray-600">Début :</label>
              <input type="date" value={planStart} onChange={(e) => setPlanStart(e.target.value)}
                className="border border-gray-300 rounded-lg px-2 py-1" />
            </span>
            {last && <span className="text-gray-600">Dernière livraison estimée : <span className="font-semibold text-gray-800">{fmt(last.fin)}</span></span>}
            <button type="button" onClick={() => setShowCA(v => !v)} data-testid="ca-previsionnel-btn"
              className={`ml-auto px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${showCA ? 'text-white border-transparent' : 'bg-white border-gray-300 text-gray-800 active:bg-gray-100'}`}
              style={showCA ? { backgroundColor: 'var(--color-primary)' } : undefined}>
              {txt('CA prévisionnel', 'Прогноз выручки', 'CA previzional', 'Прогноз виручки')}
            </button>
            {/* CA previsionnel (Pavel 28/09/2026) : somme des prix de vente HT des chantiers de la
                file (en cours + en attente), visible seulement apres appui sur le bouton. */}
            {showCA && (() => {
              const eur = (n) => `${Math.round(n).toLocaleString('fr-FR')} €`;
              const cours = fabQueue.filter(r => r.phase === 'cours'), att = fabQueue.filter(r => r.phase === 'attente');
              const sum = (rs) => rs.reduce((a, r) => a + (parseFloat(r.p.sale_price) || 0), 0);
              const sansPrix = fabQueue.filter(r => !(parseFloat(r.p.sale_price) > 0));
              return (
                <div className="basis-full mt-1 p-3 rounded-lg border border-gray-200 bg-gray-50" data-testid="ca-previsionnel">
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    <span><span className="text-gray-500">{txt('Chantiers en cours', 'Текущие стройки', 'Șantiere în curs', 'Поточні об\'єкти')} ({cours.length}) :</span> <span className="font-bold text-gray-900">{eur(sum(cours))} HT</span></span>
                    <span><span className="text-gray-500">{txt('En attente', 'В ожидании', 'În așteptare', 'В очікуванні')} ({att.length}) :</span> <span className="font-bold text-gray-900">{eur(sum(att))} HT</span></span>
                    <span><span className="text-gray-500">{txt('Total de la file', 'Итого очередь', 'Total coadă', 'Разом черга')} :</span> <span className="font-bold" style={{ color: 'var(--color-primary)' }}>{eur(sum(fabQueue))} HT</span></span>
                  </div>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-0.5 text-xs">
                    {fabQueue.map(r => {
                      const v = parseFloat(r.p.sale_price) || 0;
                      return (
                        <div key={r.p.id} className="flex justify-between gap-2 border-b border-gray-100 py-0.5">
                          <span className="truncate"><span className="text-gray-400">{r.idx + 1}.</span> {r.p.name}{r.phase === 'attente' && <span className="text-amber-700"> · {txt('en attente', 'в ожидании', 'în așteptare', 'в очікуванні')}</span>}</span>
                          {v > 0
                            ? <span className="font-medium text-gray-800 whitespace-nowrap">{eur(v)} HT</span>
                            : <span className="text-amber-700 whitespace-nowrap">{txt('prix à saisir', 'цена не указана', 'preț de completat', 'ціна не вказана')}</span>}
                        </div>
                      );
                    })}
                  </div>
                  {sansPrix.length > 0 && (
                    <p className="mt-1 text-[11px] text-amber-700">{sansPrix.length} {txt('chantier(s) sans prix de vente : à renseigner dans la fiche du chantier (« Prix de vente HT »).', 'без цены продажи: заполнить в карточке стройки.', 'fără preț de vânzare: de completat în fișa șantierului.', 'без ціни продажу: заповнити в картці об\'єкта.')}</p>
                  )}
                </div>
              );
            })()}
          </div>
        );
      })()}


        {chantiersEnCours.length === 0 ? (
          <p className="text-sm text-gray-500">
            {txt('Aucun chantier en cours pour le moment.', 'Нет текущих строек.', 'Niciun șantier în curs.', 'Немає поточних об\'єктів.')}
          </p>
        ) : (
          <>
            {totalStandbyProjects > 0 && (
              <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
                <Pause className="w-3 h-3" />
                {totalStandbyProjects} {txt('chantier(s) en stand-by, hors frise', 'на паузе, вне графика', 'în așteptare, în afara graficului', 'на паузі, поза графіком')}
              </p>
            )}


            {(() => {
              const rows = friseRows;
              if (!rows.length) return null;
              const clients = rows.filter(r => !r.interne);
              const internes = rows.filter(r => r.interne);
              const lignes = internes.length ? [...clients, { sep: true }, ...internes] : clients;
              const today = new Date(); today.setHours(0, 0, 0, 0);
              // Fenêtre FOCALISÉE sur la période active (Pavel 25/09/2026) : sans ça,
              // un seul chantier avec une date de début très ancienne étire l'axe sur
              // plus d'un an et rend la frise illisible sur téléphone. On borne à ~4
              // mois en arrière et ~2 mois en avant autour d'aujourd'hui ; les bassins
              // qui débordent de la fenêtre sont simplement rognés aux extrémités.
              const MAXBACK = 120 * DAY_MS, MAXFWD = 60 * DAY_MS;
              const earliestStart = Math.min(...rows.map(r => r.start.getTime()));
              const latestEnd = Math.max(...rows.map(r => Math.max(r.end.getTime(), r.forecast.getTime())), today.getTime());
              let min = Math.max(earliestStart, today.getTime() - MAXBACK);
              let max = Math.min(latestEnd, today.getTime() + MAXFWD);
              if (max <= min) max = today.getTime() + MAXFWD;
              min -= 5 * DAY_MS; max += 5 * DAY_MS;
              const span = Math.max(max - min, DAY_MS);
              const pos = (t) => Math.min(100, Math.max(0, ((t - min) / span) * 100));
              const ticks = [];
              const d = new Date(min); d.setDate(1); d.setHours(0, 0, 0, 0);
              if (d.getTime() < min) d.setMonth(d.getMonth() + 1);
              while (d.getTime() <= max && ticks.length < 36) { ticks.push(new Date(d)); d.setMonth(d.getMonth() + 1); }
              const todayPct = pos(today.getTime());
              // Étiquettes de mois : on n'en garde qu'une sur N pour qu'elles ne se chevauchent
              // pas (téléphone : frise étroite, période longue). Les traits fins restent tous.
              const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
              const friseWidthPx = Math.max(200, (vw < 640 ? 640 : Math.min(vw, 1280)) - (vw < 640 ? 400 : 480));
              const tickStep = Math.max(1, Math.ceil(ticks.length / Math.floor(friseWidthPx / 56)));
              const colorOf = (b) => (b === 'rouge' ? '#dc2626' : b === 'jaune' ? '#d97706' : '#16a34a');
              return (
                <div className="overflow-x-auto -mx-2 px-2">
                  <div className="min-w-[820px] sm:min-w-[880px]">
                    {/* En-tête : axe des mois */}
                    <div className="flex items-end gap-3 mb-1">
                      <div className="w-64 sm:w-72 flex-shrink-0 text-xs text-gray-400">{txt('N° · Chantier', '№ · Стройка', 'Nr · Șantier', '№ · Об\'єкт')}</div>
                      <div className="flex-1 relative h-5 border-b border-gray-200">
                        {ticks.map((t, i) => (i % tickStep !== 0 ? null : (
                          <div key={t.getTime()} className="absolute bottom-0 text-[11px] text-gray-500 -translate-x-1/2 whitespace-nowrap" style={{ left: `${pos(t.getTime())}%` }}>
                            {t.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' })}
                          </div>
                        )))}
                      </div>
                      <div className="w-28 sm:w-40 flex-shrink-0 text-xs text-gray-400 text-right">{txt('Avancement', 'Прогресс', 'Progres', 'Прогрес')}</div>
                    </div>

                    {/* Une ligne par chantier */}
                    {lignes.map((row) => {
                      if (row.sep) {
                        return (
                          <div key="sep-internes" className="flex items-center gap-3 pt-4 pb-1 mt-3 border-t-2 border-dashed border-slate-300">
                            <div className="w-64 sm:w-72 flex-shrink-0 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                              {txt('Autres chantiers (internes)', 'Прочие стройки (внутренние)', 'Alte șantiere (interne)', 'Інші об\'єкти (внутрішні)')}
                            </div>
                            <div className="flex-1 border-t border-slate-200" />
                            <div className="w-28 sm:w-40 flex-shrink-0" />
                          </div>
                        );
                      }
                      const { p, start, startEst, target, end, forecast, interne, hasDays, totalWD, expected: expectedCal } = row;
                      const fabNum = interne ? null : (fabIdxById[p.id] != null ? fabIdxById[p.id] + 1 : null);
                      const bucket = progressBucket(p);
                      const dot = colorOf(bucket);
                      const pr = p.overall_progress || 0;
                      // Dates sous le nom (Pavel 28/09/2026) : jour + mois + annee, la file court sur plus d'un an.
                      const fmtJY = (t) => new Date(t).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
                      const ecart = Math.round((forecast.getTime() - end.getTime()) / DAY_MS);
                      const enRetard = ecart > 0;
                      const ecartStr = ecart > 0
                        ? txt(`retard ${ecart} j`, `просрочка ${ecart} д`, `întârziere ${ecart} z`, `прострочення ${ecart} д`)
                        : ecart < 0 ? txt(`avance ${-ecart} j`, `опережение ${-ecart} д`, `avans ${-ecart} z`, `випередження ${-ecart} д`)
                        : txt('à l\'heure', 'в срок', 'la timp', 'вчасно');
                      const leftPct = pos(start.getTime());
                      const widthPct = Math.max(1.5, pos(end.getTime()) - leftPct);
                      const steps = Array.isArray(p.steps) ? p.steps : [];
                      let cumWD = 0;
                      const stepInfo = steps.map(st => {
                        const known = (st.days || 0) > 0;
                        const dj = known ? st.days : 1; cumWD += dj;
                        return `${st.name} : ${st.progress || 0} %` + (hasDays ? ` (${dj} j${known ? '' : ' ' + txt('par défaut', 'по умолчанию', 'implicit', 'за замовчуванням')}, ${txt('fin attendue', 'ожид. конец', 'sfârșit așteptat', 'очік. кінець')} ${addWD(start, cumWD).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })})` : '');
                      });
                      // Rythme réel (Pavel 17/09/2026) : jours ouvrés pointés sur le chantier
                      // = travaillés (bloc plein) ; jours ouvrés sans pointage = trou (hachures
                      // orange) ; week-ends ignorés. Segments fusionnés pour alléger le rendu.
                      const wd = workedDays[p.id] || { dates: [], names: {} };
                      const workedSet = new Set(wd.dates || []);
                      const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                      const segments = [];
                      let workedCount = 0;
                      for (let d = new Date(start), n = 0; d.getTime() <= today.getTime() && n < 800; d.setDate(d.getDate() + 1), n++) {
                        // Pavel 24/09/2026 : un jour POINTE sur le chantier compte toujours,
                        // meme un samedi ou un jour ferie. On ne saute que les jours non ouvres
                        // ou personne n'a pointe (ceux-la ne sont ni travailles ni un trou).
                        if (!isWorkday(d) && !workedSet.has(iso(d))) continue;
                        const key = iso(d);
                        const worked = workedSet.has(key);
                        if (worked) { workedCount++; }
                        const last = segments[segments.length - 1];
                        const t0 = d.getTime();
                        if (last && last.worked === worked && t0 - last.to <= 3 * DAY_MS) {
                          last.to = t0; last.days++; if (worked) last.who.push(...(wd.names?.[key] || []));
                        } else {
                          segments.push({ from: t0, to: t0, worked, days: 1, who: worked ? [...(wd.names?.[key] || [])] : [] });
                        }
                      }
                      // Rythme (Pavel 17/09/2026) : jours réellement pointés sur le chantier,
                      // comparés à la durée estimée du gabarit, et dépassement éventuel.
                      const estDays = Math.max(1, p.estimated_days || 30);
                      const overrun = Math.max(0, workedCount - estDays);
                      const fmtJ = (t) => new Date(t).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
                      // Date complete (Pavel 24/09/2026) : lisible dans la bulle au doigt.
                      const fmtJL = (t) => new Date(t).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
                      const rangeL = (a, b) => (a === b ? fmtJL(a) : `${fmtJL(a)} → ${fmtJL(b)}`);
                      // Attendu (Pavel 17/09/2026) : ce que le gabarit prévoit pour le nombre de
                      // jours réellement POINTÉS sur le chantier (pas les jours écoulés : l'atelier
                      // mène plusieurs chantiers de front). Réel au-dessus = bonne productivité quand
                      // on est dessus ; en dessous = pousser. Les trous, eux, se lisent sur la barre
                      // et dans le retard calendaire. Sans pointage connu : repli sur les jours écoulés.
                      const expected = hasDays
                        ? (wd.dates && wd.dates.length >= 0 && workedDays[p.id] ? Math.min(100, Math.round(workedCount / totalWD * 100)) : expectedCal)
                        : null;
                      const delta = hasDays ? pr - expected : null;
                      return (
                        <div
                          key={p.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => goToProject(p.id)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToProject(p.id); } }}
                          title={txt('Ouvrir le chantier', 'Открыть стройку', 'Deschide șantierul', 'Відкрити об\'єкт')}
                          className={`flex items-center gap-3 py-2 border-b border-gray-50 hover:bg-blue-50/60 cursor-pointer transition-colors ${interne ? 'bg-slate-50/50' : row.attente ? 'bg-amber-50/40' : ''}`}
                          data-testid={`frise-row-${p.id}`}
                        >
                          {/* N° de fabrication + fleches (Pavel 28/09/2026), puis nom + etapes */}
                          <div className="w-64 sm:w-72 flex-shrink-0 min-w-0 flex items-center gap-2">
                            {fabNum != null ? (
                              <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: 'var(--color-primary)' }} title="Numéro de fabrication">{fabNum}</span>
                                {isManager && (
                                  <div className="flex flex-col gap-0.5">
                                    <button type="button" onClick={(e) => { e.stopPropagation(); moveQueue(p.id, -1); }} disabled={fabNum === 1}
                                      className="w-9 h-7 sm:w-7 sm:h-5 rounded border border-gray-300 bg-white text-sm sm:text-xs leading-none disabled:opacity-30 active:bg-gray-100" title="Monter" aria-label="Monter">↑</button>
                                    <button type="button" onClick={(e) => { e.stopPropagation(); moveQueue(p.id, +1); }} disabled={fabNum === fabQueue.length}
                                      className="w-9 h-7 sm:w-7 sm:h-5 rounded border border-gray-300 bg-white text-sm sm:text-xs leading-none disabled:opacity-30 active:bg-gray-100" title="Descendre" aria-label="Descendre">↓</button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dot }} />
                            )}
                            <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-w-0">
                              <span className="text-sm font-medium break-words underline-offset-2 hover:underline" style={{ color: 'var(--color-primary)' }}>{p.name}</span>
                              {row.attente && <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 flex-shrink-0">{txt('en attente', 'в ожидании', 'în așteptare', 'в очікуванні')}</span>}
                            </div>
                            {p.pool_model && <div className="text-[11px] text-gray-400 truncate">{p.pool_model}</div>}
                            {/* Sous le nom (Pavel 28/09/2026) : demarrage estime, fabrication terminee, previsionnel */}
                            <div className="mt-0.5 text-[11px] leading-snug" data-testid={`frise-dates-${p.id}`}>
                              <div className="whitespace-nowrap">
                                <span className="text-gray-500">{txt('démarrage', 'начало', 'început', 'початок')}</span>{' '}
                                <span className="font-medium text-gray-800">{fmtJY(start.getTime())}</span>
                                {startEst && <span className="text-gray-400"> ({txt('est.', 'оц.', 'est.', 'оц.')})</span>}
                              </div>
                              <div className="whitespace-nowrap">
                                <span className="text-gray-500">{txt('fabrication terminée', 'конец производства', 'fabricație terminată', 'кінець виробництва')}</span>{' '}
                                <span className="font-medium text-gray-800">{fmtJY(end.getTime())}</span>
                              </div>
                              {/* L'ecart passe a la ligne s'il ne tient pas (retard a trois chiffres sur telephone). */}
                              <div className="flex flex-wrap gap-x-1">
                                <span className="whitespace-nowrap">
                                  <span className="text-gray-500">{txt('prévisionnel', 'прогноз', 'estimat', 'прогноз')}</span>{' '}
                                  <span className="font-semibold text-gray-900">{fmtJY(forecast.getTime())}</span>
                                </span>
                                <span className={`whitespace-nowrap font-medium ${enRetard ? 'text-red-600' : ecart < 0 ? 'text-green-600' : 'text-gray-400'}`}>· {ecartStr}</span>
                              </div>
                            </div>
                            {steps.length > 0 && (
                              <div className="flex gap-0.5 mt-1" title={stepInfo.join('\n')}>
                                {steps.map((s, i) => {
                                  const sp = s.progress || 0;
                                  const bg = sp >= 100 ? dot : sp > 0 ? `${dot}66` : '#e5e7eb';
                                  return <div key={s.id || i} className="h-1.5 flex-1 rounded-sm" style={{ backgroundColor: bg }} />;
                                })}
                              </div>
                            )}
                            </div>
                          </div>

                          {/* Chantiers INTERNES (Fitou, St Germain Laxis) : on garde le
                              Gantt classique (Pavel 25/09/2026). Chantiers CLIENTS : bande
                              de carrés jour par jour, plus lisible sur téléphone. */}
                          {p.is_internal ? (
                          <div className="flex-1 relative h-9">
                            {ticks.map(t => (
                              <div key={t.getTime()} className="absolute top-0 bottom-0 w-px bg-gray-100" style={{ left: `${pos(t.getTime())}%` }} />
                            ))}
                            <div className="absolute top-0 bottom-0 w-0.5 bg-red-500/80 z-10" style={{ left: `${todayPct}%` }} />
                            <div
                              className="absolute top-2 h-5 rounded-full overflow-hidden shadow-sm"
                              style={{ left: `${leftPct}%`, width: `${widthPct}%`, backgroundColor: `${dot}33`, border: `1px solid ${dot}55` }}
                              title={`${p.name} : ${start.toLocaleDateString('fr-FR')} → ${end.toLocaleDateString('fr-FR')} (${pr}%)`}
                            >
                            </div>
                            {segments.map((sg, si) => {
                              const l = pos(sg.from);
                              const w = Math.max(sg.worked ? 2.8 : 0.8, pos(sg.to + DAY_MS) - l);
                              const who = [...new Set(sg.who)].join(', ');
                              const tipTxt = sg.worked
                                ? `${rangeL(sg.from, sg.to)} : ${sg.days} j ${txt('travaillé(s)', 'отработано', 'lucrate', 'відпрацьовано')}${who ? ' (' + who + ')' : ''}`
                                : `${rangeL(sg.from, sg.to)} : ${sg.days} j ${txt('sans pointage sur ce chantier', 'без отметки на этой стройке', 'fără pontaj pe acest șantier', 'без відмітки на цьому об\'єкті')}`;
                              return (
                                <div
                                  key={si}
                                  onClick={(e) => { e.stopPropagation(); setFriseTip({ pid: p.id, left: l, text: tipTxt }); }}
                                  className="absolute top-2 h-5 cursor-pointer"
                                  style={sg.worked
                                    ? { left: `${l}%`, width: `${w}%`, backgroundColor: '#39ff14', border: '1.5px solid #15803d', borderRadius: 4, boxShadow: '0 0 0 1.5px #ffffff, 0 1px 3px rgba(0,0,0,0.4)', zIndex: 6 }
                                    : { left: `${l}%`, width: `${w}%`, backgroundColor: '#d1d5db', borderRadius: 3 }}
                                  title={tipTxt}
                                />
                              );
                            })}
                            {enRetard && (
                              <>
                                <div
                                  className="absolute top-2 h-5 rounded-r-full cursor-pointer"
                                  style={{
                                    left: `${pos(end.getTime())}%`,
                                    width: `${Math.max(0.5, pos(forecast.getTime()) - pos(end.getTime()))}%`,
                                    backgroundImage: 'repeating-linear-gradient(45deg, rgba(220,38,38,0.35) 0 4px, rgba(220,38,38,0.08) 4px 8px)',
                                    border: '1px dashed rgba(220,38,38,0.6)', borderLeft: 'none'
                                  }}
                                  onClick={(e) => { e.stopPropagation(); setFriseTip({ pid: p.id, left: pos(end.getTime()), text: `${txt('Retard prévisionnel', 'Прогноз просрочки', 'Întârziere estimată', 'Прогноз прострочення')} : ${ecart} j (${txt('prévu', 'план', 'prevăzut', 'план')} ${fmtJL(end.getTime())}, ${txt('prévision', 'прогноз', 'previziune', 'прогноз')} ${fmtJL(forecast.getTime())})` }); }}
                                  title={`${txt('Retard prévisionnel', 'Прогноз просрочки', 'Întârziere estimată', 'Прогноз прострочення')} : ${ecart} j`}
                                />
                                <div className="absolute top-1 bottom-1 w-0.5 bg-red-700 z-10" style={{ left: `${pos(forecast.getTime())}%` }} />
                              </>
                            )}
                            {friseTip && friseTip.pid === p.id && (
                              <div
                                onClick={(e) => { e.stopPropagation(); setFriseTip(null); }}
                                className="absolute z-30 px-2 py-1 rounded-md bg-slate-900 text-white text-[11px] whitespace-nowrap shadow-lg cursor-pointer"
                                style={{ left: `${Math.min(70, Math.max(0, friseTip.left || 0))}%`, top: -6 }}
                              >
                                {friseTip.text}
                              </div>
                            )}
                          </div>
                          ) : (
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap gap-1">
                              {(() => {
                                if (row.attente || start.getTime() > today.getTime()) {
                                  return <span className="text-xs font-medium text-amber-700">{txt('à venir', 'предстоит', 'urmează', 'майбутній')} · {txt('début', 'начало', 'început', 'початок')} {fmtJ(start.getTime())}</span>;
                                }
                                const cells = [];
                                const winStart = new Date(Math.max(start.getTime(), today.getTime() - 120 * DAY_MS));
                                for (let d = new Date(winStart), n = 0; d.getTime() <= today.getTime() && n < 400; d.setDate(d.getDate() + 1), n++) {
                                  const key = iso(d);
                                  const worked = workedSet.has(key);
                                  if (!isWorkday(d) && !worked) continue;
                                  cells.push({ key, worked, ts: d.getTime(), who: worked ? [...new Set(wd.names?.[key] || [])] : [] });
                                }
                                if (!cells.length) return <span className="text-xs text-gray-400">{txt('Aucun jour', 'Нет дней', 'Nicio zi', 'Немає днів')}</span>;
                                return cells.map((c, i) => {
                                  const tip = c.worked
                                    ? `${fmtJL(c.ts)} : ${txt('travaillé', 'отработано', 'lucrat', 'відпрацьовано')}${c.who.length ? ' (' + c.who.join(', ') + ')' : ''}`
                                    : `${fmtJL(c.ts)} : ${txt('non travaillé', 'не работали', 'nelucrat', 'не працювали')}`;
                                  return (
                                    <div
                                      key={c.key + i}
                                      onClick={(e) => { e.stopPropagation(); setFriseTip({ pid: p.id, text: tip }); }}
                                      title={tip}
                                      className="w-4 h-4 sm:w-5 sm:h-5 rounded-sm cursor-pointer flex-shrink-0"
                                      style={c.worked
                                        ? { backgroundColor: '#22c55e', border: '1px solid #15803d' }
                                        : { backgroundColor: '#e5e7eb', border: '1px solid #d1d5db' }}
                                    />
                                  );
                                });
                              })()}
                            </div>
                            {friseTip && friseTip.pid === p.id && (
                              <div
                                onClick={(e) => { e.stopPropagation(); setFriseTip(null); }}
                                className="mt-1 inline-block px-2 py-1 rounded-md bg-slate-900 text-white text-[11px] shadow-lg cursor-pointer"
                              >
                                {friseTip.text}
                              </div>
                            )}
                          </div>
                          )}

                          {/* Avancement + livraison */}
                          <div className="w-28 sm:w-40 flex-shrink-0 text-right leading-tight">
                            <div className="text-sm font-bold" style={{ color: dot }}>{pr} %</div>
                            {hasDays && !row.attente && (
                              <div className="text-[11px]" title={txt('Avancement que le gabarit d\'étapes prévoit à ce jour, contre le réel', 'Ожидаемый по плану прогресс против фактического', 'Progres așteptat conform planului față de cel real', 'Очікуваний за планом прогрес проти фактичного')}>
                                <span className="text-gray-500">{txt('attendu', 'ожид.', 'așteptat', 'очік.')}</span> <span className="font-semibold text-gray-700">{expected} %</span>{' '}
                                <span className="text-gray-400">({workedCount} {txt('j pointés', 'дн. отм.', 'z pontate', 'дн. відм.')} / {totalWD})</span>{' '}
                                <span className={`font-semibold px-1 rounded ${delta >= 0 ? 'bg-green-100 text-green-700' : delta > -10 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{delta >= 0 ? '+' : ''}{delta} pts</span>
                              </div>
                            )}
                            {target && (
                              <div className="text-[11px] text-gray-500">{txt('livraison prévue', 'план. сдача', 'livrare prevăzută', 'план. здача')} <span className="text-gray-700">{fmtJY(target.getTime())}</span></div>
                            )}
                            {!row.attente && (
                            <div className="text-[11px] text-gray-600 mt-0.5" title={txt('Jours pointés sur ce chantier / durée estimée du gabarit / jours travaillés au-delà de l\'estimé', 'Отработанные дни / оценка / превышение', 'Zile lucrate / estimate / depășite', 'Відпрацьовані дні / оцінка / перевищення')}>
                              <span className="font-semibold" style={{ color: dot }}>{workedCount}</span> {txt('jours travaillés', 'дн. работы', 'z lucrate', 'дн. роботи')} / {estDays} {txt('jours estimés', 'оценка дн.', 'z estimate', 'оцін. дн.')} {txt('et', 'и', 'și', 'та')} <span className={overrun > 0 ? 'font-semibold text-red-600' : ''}>{overrun}</span> {txt('jours dépassés', 'дн. сверх', 'z depășite', 'дн. понад')}
                            </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    <p className="text-[11px] text-gray-400 mt-2">
                      {txt('N° = ordre de fabrication (le 1 est le prochain) ; flèches = monter ou descendre un chantier, les dates et l\'ordre se recalculent. Ambre = chantier en attente (à venir). Sous chaque nom : démarrage (réel, ou « est. » = sa place dans la file pour un chantier pas encore commencé), fabrication terminée (démarrage + durée du gabarit, ce que le plan donne), prévisionnel (sa fin dans la file de fabrication, un chantier après l\'autre à partir de la date de début) et l\'écart entre les deux. Livraison prévue = la date saisie sur le chantier, quand il y en a une. Vert fluo = jours où quelqu\'un a pointé sur le chantier ; gris clair = jours ouvrés sans personne dessus (week-ends non comptés). Hachures rouges = le retard. Attendu = avancement que le gabarit d\'étapes (23 j ouvrés de base) prévoit pour le nombre de jours pointés sur le chantier : en dessous, il faut pousser ; au-dessus, le rythme est bon quand l\'atelier est dessus. Les chantiers internes sont regroupés en bas.',
                           'План = плановая дата сдачи (или начало + длительность). Прогноз = остаток работы по плановому темпу от сегодня. Штриховка = просрочка.',
                           'Prevăzut = data de livrare (sau start + durată). Estimat = restul de lucru la ritmul prevăzut, de azi. Hașura roșie = întârzierea.',
                           'План = планова дата здачі (або початок + тривалість). Прогноз = залишок роботи за плановим темпом від сьогодні. Штрихування = прострочення.')}
                    </p>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>

      {/* Search and Year Filter */}
      <div className="card-base p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={txt('Rechercher par nom...', 'Поиск по названию...', 'Căutare după nume...', 'Пошук за назвою...')}
              className="!pl-10 input-base"
              data-testid="search-projects-input"
            />
          </div>
          
          {/* Year Filter */}
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-[200px]" data-testid="filter-year">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{txt('Toutes les années', 'Все годы', 'Toți anii', 'Всі роки')}</SelectItem>
              {yearOptions.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Company Filter */}
          <Select value={filterCompany} onValueChange={setFilterCompany}>
            <SelectTrigger className="w-[170px]" data-testid="filter-company">
              <Building2 className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{txt('Les 2 sociétés', 'Обе компании', 'Ambele companii', 'Обидві компанії')}</SelectItem>
              <SelectItem value="La Piscine Container">🏊 LPC</SelectItem>
              <SelectItem value="Eco Bati Construction">🏗️ EBC</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Results count */}
        {(searchQuery || filterYear !== 'all' || filterStatus !== 'all') && (
          <div className="mt-3 text-sm text-gray-500 flex items-center gap-2">
            <span>
              {filteredProjects.length} {txt('chantier(s)', 'проект(ов)', 'proiect(e)', 'об\'єкт(ів)')} 
              {searchQuery && ` "${searchQuery}"`}
            </span>
            {(filterStatus !== 'all' || progressFilter !== 'all') && (
              <button
                onClick={() => { setFilterStatus('all'); setProgressFilter('all'); }}
                className="text-xs px-2 py-1 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors"
              >
                ✕ {txt('Réinitialiser le filtre', 'Сбросить фильтр', 'Resetează filtrul', 'Скинути фільтр')}
              </button>
            )}
          </div>
        )}
      </div>
      
      {/* Stats - Clickable */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <button 
          onClick={() => setFilterStatus(filterStatus === 'in_progress' ? 'all' : 'in_progress')}
          className={`stat-card cursor-pointer transition-all hover:scale-105 ${filterStatus === 'in_progress' ? 'ring-2 ring-[#0E2147] bg-[#0E2147]/5' : ''}`}
          data-testid="filter-in-progress"
        >
          <span className="stat-value" style={{ color: 'var(--color-primary)' }}>{totalActiveProjects}</span>
          <span className="stat-label">{t('inProgress')}</span>
        </button>
        <button 
          onClick={() => setFilterStatus(filterStatus === 'sav' ? 'all' : 'sav')}
          className={`stat-card cursor-pointer transition-all hover:scale-105 ${filterStatus === 'sav' ? 'ring-2 ring-orange-500 bg-orange-50' : ''}`}
          data-testid="filter-sav"
        >
          <span className="stat-value text-orange-600">{totalSavProjects}</span>
          <span className="stat-label flex items-center justify-center gap-1">
            <Wrench className="w-3 h-3" />
            SAV
          </span>
        </button>
        <button 
          onClick={() => setFilterStatus(filterStatus === 'maintenance' ? 'all' : 'maintenance')}
          className={`stat-card cursor-pointer transition-all hover:scale-105 ${filterStatus === 'maintenance' ? 'ring-2 ring-cyan-500 bg-cyan-50' : ''}`}
          data-testid="filter-maintenance"
        >
          <span className="stat-value text-cyan-600">{totalMaintenanceProjects}</span>
          <span className="stat-label flex items-center justify-center gap-1">
            <Droplets className="w-3 h-3" />
            {txt('Entretien', 'Обслуживание', 'Întreținere', 'Обслуговування')}
          </span>
        </button>
        <button
          onClick={() => setFilterStatus(filterStatus === 'standby' ? 'all' : 'standby')}
          className={`stat-card cursor-pointer transition-all hover:scale-105 ${filterStatus === 'standby' ? 'ring-2 ring-slate-500 bg-slate-100' : ''}`}
          data-testid="filter-standby"
        >
          <span className="stat-value text-slate-600">{totalStandbyProjects}</span>
          <span className="stat-label flex items-center justify-center gap-1">
            <Pause className="w-3 h-3" />
            Stand-by
          </span>
        </button>
        <button 
          onClick={() => setFilterStatus(filterStatus === 'archived' ? 'all' : 'archived')}
          className={`stat-card cursor-pointer transition-all hover:scale-105 ${filterStatus === 'archived' ? 'ring-2 ring-gray-400 bg-gray-100' : ''}`}
          data-testid="filter-archived"
        >
          <span className="stat-value text-gray-500">{totalArchivedProjects}</span>
          <span className="stat-label">{txt('Archivés', 'Архив', 'Arhivat', 'Архів')}</span>
        </button>
      </div>
      
      {/* Add/Edit Dialog */}
      <Dialog open={showAdd} onOpenChange={closeDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: 'var(--color-primary)' }}>
              {editProject ? t('update') : t('newProject')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Cover photo */}
            <div className="form-group">
              <Label className="form-label">{txt('Photo de profil du chantier', 'Фото проекта', 'Foto de profil șantier', 'Фото проекту')}</Label>
              {form.cover_photo ? (
                <div className="relative rounded-xl overflow-hidden">
                  <img
                    src={form.cover_photo}
                    alt="Cover"
                    className="w-full h-36 object-cover cursor-zoom-in"
                    onClick={() => setPreviewDoc(form.cover_photo)}
                  />
                  <div className="absolute bottom-0 left-0 right-0 flex gap-2 p-2 bg-gradient-to-t from-black/60 to-transparent">
                    <label className="flex-1 flex items-center justify-center gap-1 bg-white/90 hover:bg-white text-gray-700 text-xs font-medium py-1.5 rounded-lg cursor-pointer transition-colors">
                      <Camera className="w-3.5 h-3.5" />
                      {txt('Modifier', 'Изменить', 'Modifică', 'Змінити')}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleDocumentUpload(e, 'cover_photo')}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, cover_photo: '' })}
                      className="flex-1 flex items-center justify-center gap-1 bg-red-500 hover:bg-red-600 text-white text-xs font-medium py-1.5 rounded-lg transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                      {txt('Supprimer', 'Удалить', 'Șterge', 'Видалити')}
                    </button>
                  </div>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                  <Camera className="w-7 h-7 text-gray-400 mb-1" />
                  <span className="text-xs text-gray-500">{txt('Ajouter une photo', 'Добавить фото', 'Adaugă o fotografie', 'Додати фото')}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleDocumentUpload(e, 'cover_photo')}
                  />
                </label>
              )}
            </div>

            <div className="form-group">
              <Label className="form-label">{t('projectName')} *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Piscine Container Villa Martin"
                className="input-base"
                data-testid="project-name-input"
              />
            </div>
            
            {/* Company selector - Only for managers */}
            {isManager && (
              <div className="form-group">
                <Label className="form-label flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  {txt('Société', 'Компания', 'Companie', 'Компанія')}
                </Label>
                <Select value={form.company} onValueChange={(v) => setForm({ ...form, company: v })}>
                  <SelectTrigger className="input-base" data-testid="project-company-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANIES.map(company => (
                      <SelectItem key={company.id} value={company.id}>
                        <span className="flex items-center gap-2">
                          <span>{company.icon}</span>
                          <span>{company.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="form-group">
              <Label className="form-label">{txt('Adresse du client', 'Адрес клиента', 'Adresa clientului', 'Адреса клієнта')}</Label>
              <Input
                value={form.client_address}
                onChange={(e) => setForm({ ...form, client_address: e.target.value })}
                placeholder="Ex: 123 Rue de la Paix, 75001 Paris"
                className="input-base"
                data-testid="project-address-input"
              />
            </div>
            
            <div className="form-group">
              <Label className="form-label">{txt('Téléphone du client', 'Телефон клиента', 'Telefonul clientului', 'Телефон клієнта')}</Label>
              <Input
                value={form.client_phone}
                onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                placeholder="Ex: 06 12 34 56 78"
                className="input-base"
                data-testid="project-phone-input"
              />
            </div>

            <div className="form-group">
              <Label className="form-label">{txt('Email du client', 'Email клиента', 'Emailul clientului', 'Email клієнта')}</Label>
              <Input
                type="email"
                value={form.client_email}
                onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                placeholder="Ex: client@email.com"
                className="input-base"
                data-testid="project-email-input"
              />
            </div>
            
            <div className="form-group">
              <Label className="form-label">{t('description')}</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={t('description')}
                className="input-base min-h-[80px] py-3"
                data-testid="project-description-input"
              />
            </div>

            {/* Pool photo */}
            <div className="form-group">
              <Label className="form-label flex items-center gap-2">
                <Droplets className="w-4 h-4" />
                {txt('Photo de la piscine', 'Фото бассейна', 'Fotografia piscinei', 'Фото басейну')}
              </Label>
              {form.pool_photo ? (
                <div className="relative">
                  <img src={form.pool_photo} alt="Piscine" className="w-full h-32 object-cover rounded-xl border cursor-pointer" onClick={() => setPreviewDoc(form.pool_photo)} />
                  <button type="button" onClick={() => setForm({ ...form, pool_photo: '' })} className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-cyan-300 rounded-xl cursor-pointer hover:bg-cyan-50 transition-colors">
                  <Camera className="w-6 h-6 text-cyan-400 mb-1" />
                  <span className="text-xs text-cyan-500">{txt('Photo de la piscine', 'Фото бассейна', 'Foto piscină', 'Фото басейну')}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleDocumentUpload(e, 'pool_photo')} />
                </label>
              )}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="form-group">
                <Label className="form-label">{t('startDate')}</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="input-base"
                  data-testid="project-start-date-input"
                />
              </div>
              
              <div className="form-group">
                <Label className="form-label">{t('estimatedDays')}</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.estimated_days}
                  onChange={(e) => setForm({ ...form, estimated_days: parseInt(e.target.value) || 1 })}
                  className="input-base"
                  data-testid="project-duration-input"
                />
              </div>
            </div>

            {/* Modele / taille du bassin : sert au rapport de rentabilite (temps et cout par taille) */}
            <div className="form-group">
              <Label className="form-label">
                {txt('Modèle / taille du bassin', 'Модель / размер бассейна', 'Model / dimensiune bazin', 'Модель / розмір басейну')}
              </Label>
              <Input
                type="text"
                list="pool-model-suggestions"
                value={form.pool_model}
                onChange={(e) => setForm({ ...form, pool_model: e.target.value })}
                className="input-base"
                placeholder={txt('ex. Confort 6m, 8m, 12m', 'напр. Confort 6m, 8m, 12m', 'ex. Confort 6m, 8m, 12m', 'напр. Confort 6m, 8m, 12m')}
                data-testid="project-pool-model-input"
              />
              <datalist id="pool-model-suggestions">
                <option value="Éco 4,30m" /><option value="Éco 5m" />
                <option value="Confort 6m" /><option value="Confort 8m" />
                <option value="Confort Plus 8m" /><option value="Confort 12m" /><option value="Confort Plus 12m" />
              </datalist>
              <p className="text-xs text-slate-400 mt-1">
                {txt('Laissez vide si ce n\'est pas une piscine (dépôt, atelier...). Rempli, ce chantier entre dans le rapport de rentabilité par taille.',
                     'Оставьте пустым, если это не бассейн (склад, цех...). Заполнено: объект попадает в отчёт рентабельности.',
                     'Lăsați gol dacă nu este piscină (depozit, atelier...). Completat, șantierul intră în raportul de rentabilitate.',
                     'Залиште порожнім, якщо це не басейн (склад, цех...). Заповнено: об\'єкт потрапляє у звіт рентабельності.')}
              </p>
            </div>

            {/* Projet interne (depot, atelier, maison Fitou...) : bloc a part dans
                le rapport de rentabilite, jamais compte comme une piscine. */}
            <div className="form-group">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!form.is_internal}
                  onChange={(e) => setForm({ ...form, is_internal: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-[#0E2147]"
                  data-testid="project-internal-checkbox"
                />
                <span>
                  <span className="form-label block">
                    {txt('Projet interne (dépôt, atelier, maison Fitou...)', 'Внутренний проект (склад, цех, дом Fitou...)', 'Proiect intern (depozit, atelier, casa Fitou...)', 'Внутрішній проєкт (склад, цех, будинок Fitou...)')}
                  </span>
                  <span className="text-xs text-slate-400">
                    {txt('Ce n\'est pas une piscine client : le rapport de rentabilité l\'affiche à part.',
                         'Это не бассейн клиента: в отчёте рентабельности он показан отдельно.',
                         'Nu este o piscină client: raportul de rentabilitate îl afișează separat.',
                         'Це не басейн клієнта: у звіті рентабельності він показаний окремо.')}
                  </span>
                </span>
              </label>
            </div>

            {/* Prix de vente HT + couts matiere : servent au NET par piscine (rapport). */}
            <div className="form-group">
              <Label className="form-label">
                {txt('Prix de vente HT (€)', 'Цена продажи без НДС (€)', 'Preț de vânzare fără TVA (€)', 'Ціна продажу без ПДВ (€)')}
              </Label>
              <Input
                type="number"
                min="0"
                value={form.sale_price}
                onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                className="input-base"
                placeholder={txt('ex. 32000', 'напр. 32000', 'ex. 32000', 'напр. 32000')}
                data-testid="project-sale-price-input"
              />
            </div>

            <div className="form-group">
              <Label className="form-label">
                {txt('Coûts matière première (détail par ligne)', 'Затраты на сырьё (по строкам)', 'Costuri materie primă (pe linii)', 'Витрати на сировину (по рядках)')}
              </Label>
              <div className="space-y-2" data-testid="material-costs-editor">
                {(form.material_costs || []).map((m, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={m.label}
                      onChange={(e) => setForm({ ...form, material_costs: form.material_costs.map((it, i) => i === idx ? { ...it, label: e.target.value } : it) })}
                      className="input-base flex-1"
                      placeholder={txt('Poste (structure, membrane, PAC...)', 'Позиция', 'Post', 'Позиція')}
                    />
                    <Input
                      type="number"
                      value={m.amount}
                      onChange={(e) => setForm({ ...form, material_costs: form.material_costs.map((it, i) => i === idx ? { ...it, amount: e.target.value } : it) })}
                      className="input-base w-28"
                      placeholder="€"
                    />
                    <button type="button" onClick={() => setForm({ ...form, material_costs: form.material_costs.filter((_, i) => i !== idx) })} className="text-red-500 p-1" aria-label="supprimer">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-1">
                  <button type="button" onClick={() => setForm({ ...form, material_costs: [...(form.material_costs || []), { label: '', amount: '' }] })} className="text-sm underline text-slate-600" data-testid="material-cost-add">
                    + {txt('Ajouter une ligne', 'Добавить строку', 'Adaugă o linie', 'Додати рядок')}
                  </button>
                  <span className="text-sm font-semibold text-slate-800">
                    {Math.round((form.material_costs || []).reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0)).toLocaleString('fr-FR')} €
                  </span>
                </div>
              </div>
            </div>

            <div className="form-group">
              <Label className="form-label">
                {txt('Budget de coût (optionnel, € HT)', 'Бюджет затрат (необязательно, € без НДС)', 'Buget de cost (opțional, € fără TVA)', 'Бюджет витрат (необов\'язково, € без ПДВ)')}
              </Label>
              <Input
                type="number"
                min="0"
                value={form.budget_cost}
                onChange={(e) => setForm({ ...form, budget_cost: e.target.value })}
                className="input-base"
                placeholder={txt('laissez vide = calcul automatique', 'пусто = авторасчет', 'gol = calcul automat', 'порожньо = авторозрахунок')}
                data-testid="project-budget-cost-input"
              />
              <p className="text-xs text-slate-400 mt-1">
                {txt('Ce qu\'on DOIT dépenser sur ce chantier. Vide, le rapport l\'estime tout seul (matière + main-d\'œuvre estimée + charges fixes).',
                     'Сколько ДОЛЖНЫ потратить. Пусто: отчет оценит сам.',
                     'Cât trebuie cheltuit. Gol: raportul estimează singur.',
                     'Скільки МАЄМО витратити. Порожньо: звіт оцінить сам.')}
              </p>
            </div>

            {/* Target delivery date */}
            <div className="form-group">
              <Label className="form-label">
                {txt('Date de livraison prévue (optionnel)', 'Плановая дата сдачи (необязательно)', 'Data de livrare prevăzută (opțional)', 'Планова дата здачі (необов\'язково)')}
              </Label>
              <Input
                type="date"
                value={form.target_date || ''}
                onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                className="input-base"
                data-testid="project-target-date-input"
              />
              <p className="text-xs text-gray-400 mt-1">
                {txt('Un badge "En retard" apparaît si cette date est dépassée, et tu reçois une alerte.', 'Значок "Опоздание" появится после этой даты, и придёт уведомление.', 'Un badge "Întârziat" apare dacă data este depășită și primești o alertă.', 'Значок "Запізнення" з\'явиться після цієї дати, і прийде сповіщення.')}
              </p>
            </div>

            {/* Partie du container (édition d'un chantier piscine, Pavel 25/09/2026) :
                ajuste l'habillage PVC (haute 2 j / basse 1,5 j) + 0,5 j par plage
                immergée et par volet roulant, et recalcule le délai. */}
            {editProject && (form.company || 'La Piscine Container') !== 'Eco Bati Construction' && (
              <div className="form-group space-y-2">
                <Label className="form-label">
                  {txt('Partie du container', 'Часть контейнера', 'Partea containerului', 'Частина контейнера')}
                </Label>
                <div className="flex gap-2">
                  {[['haute', txt('Partie haute', 'Верхняя часть', 'Partea de sus', 'Верхня частина')],
                    ['basse', txt('Partie basse', 'Нижняя часть', 'Partea de jos', 'Нижня частина')]].map(([v, label]) => (
                    <button key={v} type="button" onClick={() => setForm({ ...form, container_part: v })}
                      className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-all ${(form.container_part || 'haute') === v ? 'text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`}
                      style={(form.container_part || 'haute') === v ? { backgroundColor: 'var(--color-primary)' } : {}}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex flex-col gap-1 text-sm pt-1">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input type="checkbox" className="w-4 h-4 accent-[#0E2147]"
                      checked={!!form.plage_immergee}
                      onChange={(e) => setForm({ ...form, plage_immergee: e.target.checked })} />
                    <span>{txt('Plage immergée (+0,5 j habillage)', 'Погружной пляж (+0,5 дн.)', 'Plajă imersată (+0,5 z)', 'Занурений пляж (+0,5 дн.)')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input type="checkbox" className="w-4 h-4 accent-[#0E2147]"
                      checked={!!form.volet_roulant}
                      onChange={(e) => setForm({ ...form, volet_roulant: e.target.checked })} />
                    <span>{txt('Volet roulant (+0,5 j habillage)', 'Роллета (+0,5 дн.)', 'Rulou (+0,5 z)', 'Ролета (+0,5 дн.)')}</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  {txt('Habillage PVC : partie haute 2 j, partie basse 1,5 j, + 0,5 j par option. Le délai se recalcule à l\'enregistrement.',
                       'Обшивка ПВХ: верх 2 дн., низ 1,5 дн., + 0,5 дн. за опцию.',
                       'Placare PVC: sus 2 z, jos 1,5 z, + 0,5 z pe opțiune.',
                       'Обшивка ПВХ: верх 2 дн., низ 1,5 дн., + 0,5 дн. за опцію.')}
                </p>
              </div>
            )}

            {/* Gabarit d'étapes (création seulement, chantiers piscine) : taille + options */}
            {!editProject && (form.company || 'La Piscine Container') !== 'Eco Bati Construction' && (
              <div className="form-group space-y-2">
                <Label className="form-label">
                  {txt('Étapes de départ', 'Начальные этапы', 'Etape de pornire', 'Початкові етапи')}
                </Label>
                <Select value={stepSize} onValueChange={setStepSize}>
                  <SelectTrigger className="input-base" data-testid="project-template-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="430">{txt('Piscine 4,30 m (22 j ouvrés)', 'Бассейн 4,30 м (22 раб. дн.)', 'Piscină 4,30 m (22 z lucr.)', 'Басейн 4,30 м (22 роб. дн.)')}</SelectItem>
                    <SelectItem value="5">{txt('Piscine 5 m (23 j ouvrés)', 'Бассейн 5 м (23 раб. дн.)', 'Piscină 5 m (23 z lucr.)', 'Басейн 5 м (23 роб. дн.)')}</SelectItem>
                    <SelectItem value="6">{txt('Piscine 6 m (23 j ouvrés)', 'Бассейн 6 м (23 раб. дн.)', 'Piscină 6 m (23 z lucr.)', 'Басейн 6 м (23 роб. дн.)')}</SelectItem>
                    <SelectItem value="grand">{txt('Piscine 8 à 12 m (33 j ouvrés)', 'Бассейн 8–12 м (33 раб. дн.)', 'Piscină 8–12 m (33 z lucr.)', 'Басейн 8–12 м (33 роб. дн.)')}</SelectItem>
                    <SelectItem value="vide">{txt('Aucune étape (je les crée moi-même)', 'Без этапов (создам сам)', 'Fără etape (le creez singur)', 'Без етапів (створю сам)')}</SelectItem>
                  </SelectContent>
                </Select>
                {stepSize !== 'vide' && stepSize !== 'grand' && (
                  <div className="pt-1">
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      {txt('Partie du container (habillage PVC : haute 2 j, basse 1,5 j)', 'Часть контейнера (обшивка ПВХ: верх 2 дн., низ 1,5 дн.)', 'Partea containerului (placare PVC: sus 2 z, jos 1,5 z)', 'Частина контейнера (обшивка ПВХ: верх 2 дн., низ 1,5 дн.)')}
                    </div>
                    <div className="flex gap-2">
                      {[['haute', txt('Partie haute', 'Верхняя часть', 'Partea de sus', 'Верхня частина')],
                        ['basse', txt('Partie basse', 'Нижняя часть', 'Partea de jos', 'Нижня частина')]].map(([v, label]) => (
                        <button key={v} type="button" onClick={() => setContainerPart(v)}
                          className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-all ${containerPart === v ? 'text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`}
                          style={containerPart === v ? { backgroundColor: 'var(--color-primary)' } : {}}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {stepSize !== 'vide' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm pt-1">
                    {[
                      ['volet', txt('Volet roulant (+1,5 j)', 'Роллета (+1,5 дн.)', 'Rulou (+1,5 z)', 'Ролета (+1,5 дн.)')],
                      ['plage', txt('Plage immergée (+0,5 j)', 'Погружной пляж (+0,5 дн.)', 'Plajă imersată (+0,5 z)', 'Занурений пляж (+0,5 дн.)')],
                      ['ncc', txt('Nage à contre-courant (+1 j)', 'Противоток (+1 дн.)', 'Înot contracurent (+1 z)', 'Протитечія (+1 дн.)')],
                      ['habillage', txt('Habillage bois des margelles (+2 j)', 'Деревянная обшивка бортов (+2 дн.)', 'Placare lemn margini (+2 z)', 'Дерев\'яне облицювання бортів (+2 дн.)')],
                      ['terrasse', txt('Terrasse coulissante (+3 j)', 'Раздвижная терраса (+3 дн.)', 'Terasă culisantă (+3 z)', 'Розсувна тераса (+3 дн.)')],
                    ].map(([k, label]) => (
                      <label key={k} className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-[#0E2147]"
                          checked={!!stepOptions[k]}
                          onChange={(e) => setStepOptions({ ...stepOptions, [k]: e.target.checked })}
                          data-testid={`project-option-${k}`}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                )}
                {stepSize !== 'vide' && (
                  <p className="text-xs text-gray-500">
                    {txt('Plan de fabrication :', 'План производства:', 'Plan de fabricație:', 'План виробництва:')} <span className="font-semibold text-gray-700">{gabaritTotal} {txt('jours ouvrés', 'раб. дней', 'zile lucrătoare', 'роб. днів')}</span>
                  </p>
                )}
              </div>
            )}

            {/* Documents section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold mb-3" style={{ color: 'var(--color-primary)' }}>
                {txt('Documents', 'Документы', 'Documente', 'Документи')}
              </h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Sketch photo */}
                <div className="form-group">
                  <Label className="form-label text-sm">
                    {txt('Croquis', 'Эскиз', 'Schiță', 'Ескіз')}
                  </Label>
                  <div className="relative">
                    {form.sketch_photo ? (
                      <div className="relative">
                        <img src={form.sketch_photo} alt="Croquis" className="w-full h-24 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, sketch_photo: '' })}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                        <FileImage className="w-6 h-6 text-gray-400 mb-1" />
                        <span className="text-xs text-gray-500">
                          {txt('Ajouter', 'Добавить', 'Adaugă', 'Додати')}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleDocumentUpload(e, 'sketch_photo')}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Delivery note photo */}
                <div className="form-group">
                  <Label className="form-label text-sm">
                    {txt('Bon de livraison', 'Накладная', 'Bon de livrare', 'Накладна')}
                  </Label>
                  <div className="relative">
                    {form.delivery_note_photo ? (
                      <div className="relative">
                        <img src={form.delivery_note_photo} alt="Bon de livraison" className="w-full h-24 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, delivery_note_photo: '' })}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                        <FileText className="w-6 h-6 text-gray-400 mb-1" />
                        <span className="text-xs text-gray-500">
                          {txt('Ajouter', 'Добавить', 'Adaugă', 'Додати')}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleDocumentUpload(e, 'delivery_note_photo')}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={closeDialog}
                variant="outline"
                className="btn-secondary flex-1"
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={handleSubmit}
                className="btn-primary flex-1"
                data-testid="save-project-btn"
              >
                {editProject ? t('update') : t('create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Projects List */}
      {filteredProjects.length === 0 ? (
        <div className="card-base p-12">
          <div className="empty-state">
            <FolderKanban className="empty-state-icon" />
            <p className="empty-state-text">{searchQuery ? txt('Aucun résultat', 'Ничего не найдено', 'Nimic găsit', 'Нічого не знайдено') : t('noProjects')}</p>
            {/* Only manager can create projects */}
            {!searchQuery && isManager && (
              <Button
                onClick={() => setShowAdd(true)}
                className="btn-primary mt-4"
              >
                {t('createFirst')}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Active Projects */}
          {activeProjects.length > 0 && (
            <div>
              <h2 className="section-title">{t('inProgress')}</h2>
              <div className="space-y-4">
                {activeProjects.map((project, index) => (
                  <ProjectCard
                    key={project.id}
                    labor={laborStats[project.id]}
                    onAddStepPhoto={handleAddStepPhoto}
                    onDeleteStepPhoto={handleDeleteStepPhoto}
                    project={project}
                    index={index}
                    locale={locale}
                    t={t}
                    language={language}
                    expanded={expandedProject === project.id}
                    onOuvrirDocument={ouvrirDocument}
                    onToggle={() => toggleExpand(project.id)}
                    onEdit={() => openEdit(project)}
                    onDelete={() => handleDelete(project.id, project.name)}
                    onShare={() => handleShare(project)}
                    onAddMaterial={() => navigate('/materials', { state: { addFor: project.id } })}                    onDailyPhotos={() => openDailyPhotos(project)}
                    onToggleStandby={() => handleToggleStandby(project)}
                    onStepProgress={handleStepProgress}
                    onAddStep={handleAddStep}
                    onDeleteStep={handleDeleteStep}
                    onRenameStep={handleRenameStep}
                    onEditStepDays={handleEditDays}
                    onContainerChange={handleContainerChange}
                    onReorderSteps={handleReorderSteps}
                    setPreviewDoc={setPreviewDoc}
                    isManager={isManager}
                    onCoverPhotoChange={handleCoverPhotoChange}
                    onCoverPhotoDelete={handleCoverPhotoDelete}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* SAV Projects */}
          {savProjects.length > 0 && (
            <div>
              <h2 className="section-title flex items-center gap-2">
                <Wrench className="w-5 h-5 text-orange-600" />
                SAV
              </h2>
              <div className="space-y-4">
                {savProjects.map((project, index) => (
                  <ProjectCard
                    key={project.id}
                    labor={laborStats[project.id]}
                    onAddStepPhoto={handleAddStepPhoto}
                    onDeleteStepPhoto={handleDeleteStepPhoto}
                    project={project}
                    index={index}
                    locale={locale}
                    t={t}
                    language={language}
                    expanded={expandedProject === project.id}
                    onOuvrirDocument={ouvrirDocument}
                    onToggle={() => toggleExpand(project.id)}
                    onEdit={() => openEdit(project)}
                    onDelete={() => handleDelete(project.id, project.name)}
                    onShare={() => handleShare(project)}
                    onAddMaterial={() => navigate('/materials', { state: { addFor: project.id } })}                    onDailyPhotos={() => openDailyPhotos(project)}
                    onToggleStandby={() => handleToggleStandby(project)}
                    onStepProgress={handleStepProgress}
                    onAddStep={handleAddStep}
                    onDeleteStep={handleDeleteStep}
                    onRenameStep={handleRenameStep}
                    onEditStepDays={handleEditDays}
                    onContainerChange={handleContainerChange}
                    onReorderSteps={handleReorderSteps}
                    setPreviewDoc={setPreviewDoc}
                    isManager={isManager}
                    onCoverPhotoChange={handleCoverPhotoChange}
                    onCoverPhotoDelete={handleCoverPhotoDelete}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Maintenance Projects */}
          {maintenanceProjects.length > 0 && (
            <div>
              <h2 className="section-title flex items-center gap-2">
                <Droplets className="w-5 h-5 text-cyan-600" />
                {txt('Entretien de piscine', 'Обслуживание бассейнов', 'Întreținere piscine', 'Обслуговування басейнів')}
              </h2>
              <div className="space-y-4">
                {maintenanceProjects.map((project, index) => (
                  <ProjectCard
                    key={project.id}
                    labor={laborStats[project.id]}
                    onAddStepPhoto={handleAddStepPhoto}
                    onDeleteStepPhoto={handleDeleteStepPhoto}
                    project={project}
                    index={index}
                    locale={locale}
                    t={t}
                    language={language}
                    expanded={expandedProject === project.id}
                    onOuvrirDocument={ouvrirDocument}
                    onToggle={() => toggleExpand(project.id)}
                    onEdit={() => openEdit(project)}
                    onDelete={() => handleDelete(project.id, project.name)}
                    onShare={() => handleShare(project)}
                    onAddMaterial={() => navigate('/materials', { state: { addFor: project.id } })}                    onDailyPhotos={() => openDailyPhotos(project)}
                    onToggleStandby={() => handleToggleStandby(project)}
                    onStepProgress={handleStepProgress}
                    onAddStep={handleAddStep}
                    onDeleteStep={handleDeleteStep}
                    onRenameStep={handleRenameStep}
                    onEditStepDays={handleEditDays}
                    onContainerChange={handleContainerChange}
                    onReorderSteps={handleReorderSteps}
                    setPreviewDoc={setPreviewDoc}
                    isManager={isManager}
                    onCoverPhotoChange={handleCoverPhotoChange}
                    onCoverPhotoDelete={handleCoverPhotoDelete}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Stand-by Projects (chantiers en pause) */}
          {standbyProjects.length > 0 && (
            <div>
              <h2 className="section-title flex items-center gap-2">
                <Pause className="w-5 h-5 text-slate-600" />
                {txt('Chantiers en stand-by', 'Стройки на паузе', 'Șantiere în așteptare', 'Об\'єкти на паузі')}
              </h2>
              <div className="space-y-4">
                {standbyProjects.map((project, index) => (
                  <ProjectCard
                    key={project.id}
                    labor={laborStats[project.id]}
                    onAddStepPhoto={handleAddStepPhoto}
                    onDeleteStepPhoto={handleDeleteStepPhoto}
                    project={project}
                    index={index}
                    locale={locale}
                    t={t}
                    language={language}
                    expanded={expandedProject === project.id}
                    onOuvrirDocument={ouvrirDocument}
                    onToggle={() => toggleExpand(project.id)}
                    onEdit={() => openEdit(project)}
                    onDelete={() => handleDelete(project.id, project.name)}
                    onShare={() => handleShare(project)}
                    onAddMaterial={() => navigate('/materials', { state: { addFor: project.id } })}                    onDailyPhotos={() => openDailyPhotos(project)}
                    onToggleStandby={() => handleToggleStandby(project)}
                    onStepProgress={handleStepProgress}
                    onAddStep={handleAddStep}
                    onDeleteStep={handleDeleteStep}
                    onRenameStep={handleRenameStep}
                    onEditStepDays={handleEditDays}
                    onContainerChange={handleContainerChange}
                    onReorderSteps={handleReorderSteps}
                    setPreviewDoc={setPreviewDoc}
                    isManager={isManager}
                    onCoverPhotoChange={handleCoverPhotoChange}
                    onCoverPhotoDelete={handleCoverPhotoDelete}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Archived Projects - Collapsible */}
          {archivedProjects.length > 0 && (
            <div>
              <button 
                onClick={() => setArchivesExpanded(!archivesExpanded)}
                className="section-title flex items-center gap-2 w-full text-left hover:bg-gray-50 rounded-lg p-2 -ml-2 transition-colors"
              >
                <Archive className="w-5 h-5" />
                {txt('Archivés', 'Архив', 'Arhivat', 'Архів')}
                <span className="text-sm font-normal text-gray-400">({archivedProjects.length})</span>
                <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${archivesExpanded ? 'rotate-180' : ''}`} />
              </button>
              
              {archivesExpanded && (
                <div className="space-y-4 mt-4">
                  {archivedProjects.map((project, index) => (
                    <ProjectCard
                      key={project.id}
                      labor={laborStats[project.id]}
                      onAddStepPhoto={handleAddStepPhoto}
                      onDeleteStepPhoto={handleDeleteStepPhoto}
                      project={project}
                      index={index}
                      locale={locale}
                      t={t}
                      language={language}
                      expanded={expandedProject === project.id}
                      onOuvrirDocument={ouvrirDocument}
                      onToggle={() => toggleExpand(project.id)}
                      onEdit={() => openEdit(project)}
                      onDelete={() => handleDelete(project.id, project.name)}
                      onShare={() => handleShare(project)}
                      onDailyPhotos={() => openDailyPhotos(project)}
                    onToggleStandby={() => handleToggleStandby(project)}
                      onStepProgress={handleStepProgress}
                      onAddStep={handleAddStep}
                      onDeleteStep={handleDeleteStep}
                      onRenameStep={handleRenameStep}
                      onEditStepDays={handleEditDays}
                      onContainerChange={handleContainerChange}
                      onReorderSteps={handleReorderSteps}
                      setPreviewDoc={setPreviewDoc}
                      onRestore={() => handleRestore(project.id)}
                      onPermanentDelete={() => handlePermanentDelete(project.id, project.name)}
                      isArchived={true}
                      isManager={isManager}
                      onCoverPhotoChange={handleCoverPhotoChange}
                      onCoverPhotoDelete={handleCoverPhotoDelete}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add Step Dialog */}
      <Dialog open={showAddStep} onOpenChange={setShowAddStep}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: 'var(--color-primary)' }}>
              {t('addStep')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="form-group">
              <Label className="form-label">{t('stepName')} *</Label>
              <Input
                value={newStepName}
                onChange={(e) => setNewStepName(e.target.value)}
                placeholder={txt('Entrez le nom de l\'étape', 'Введите название этапа', 'Introduceți numele etapei', 'Введіть назву етапу')}
                className="input-base"
                data-testid="add-step-input"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && submitAddStep()}
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowAddStep(false)}
                variant="outline"
                className="btn-secondary flex-1"
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={submitAddStep}
                disabled={!newStepName.trim()}
                className="btn-primary flex-1"
                data-testid="confirm-add-step-btn"
              >
                {t('create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename Step Dialog */}
      <Dialog open={showRenameStep} onOpenChange={setShowRenameStep}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: 'var(--color-primary)' }}>
              {txt('Renommer l\'étape', 'Переименовать этап', 'Redenumește etapa', 'Перейменувати етап')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="form-group">
              <Label className="form-label">{t('stepName')} *</Label>
              <Input
                value={renameStepData.currentName}
                onChange={(e) => setRenameStepData({...renameStepData, currentName: e.target.value})}
                className="input-base"
                data-testid="rename-step-input"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && submitRenameStep()}
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowRenameStep(false)}
                variant="outline"
                className="btn-secondary flex-1"
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={submitRenameStep}
                disabled={!renameStepData.currentName.trim()}
                className="btn-primary flex-1"
                data-testid="confirm-rename-step-btn"
              >
                {t('update')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: 'var(--color-primary)' }}>
              <Share2 className="w-5 h-5 inline mr-2" />
              {txt('Partager', 'Поделиться', 'Partajează', 'Поділитися')}
            </DialogTitle>
          </DialogHeader>
          
          {shareProject && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <h3 className="font-bold mb-2" style={{ color: 'var(--color-primary)' }}>{shareProject.name}</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>📊 Progression: {shareProject.overall_progress || 0}%</p>
                  <p>✅ Étapes: {shareProject.steps?.filter(s => s.progress === 100).length || 0}/{shareProject.steps?.length || 0}</p>
                  <p>📅 Début: {new Date(shareProject.start_date).toLocaleDateString(locale)}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <button
                  onClick={shareViaWhatsApp}
                  className="w-full p-4 flex items-center gap-4 rounded-xl border-2 border-gray-200 hover:border-[#25D366] hover:bg-[#25D366]/5 transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold" style={{ color: 'var(--color-primary)' }}>WhatsApp</p>
                    <p className="text-sm text-gray-500">{txt('Envoyer au client', 'Отправить клиенту', 'Trimite clientului', 'Надіслати клієнту')}</p>
                  </div>
                </button>
                
                <button
                  onClick={shareViaEmail}
                  className="w-full p-4 flex items-center gap-4 rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold" style={{ color: 'var(--color-primary)' }}>Email</p>
                    <p className="text-sm text-gray-500">{txt('Envoyer par email', 'Отправить по почте', 'Trimite prin email', 'Надіслати електронною поштою')}</p>
                  </div>
                </button>
                
                <button
                  onClick={copyToClipboard}
                  className="w-full p-4 flex items-center gap-4 rounded-xl border-2 border-gray-200 hover:border-gray-400 hover:bg-gray-50 transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-gray-500 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold" style={{ color: 'var(--color-primary)' }}>{txt('Copier', 'Копировать', 'Copiază', 'Копіювати')}</p>
                    <p className="text-sm text-gray-500">{txt('Dans le presse-papier', 'В буфер обмена', 'În clipboard', 'У буфер обміну')}</p>
                  </div>
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600">
              {txt('Archiver le chantier ?', 'Архивировать проект?', 'Arhivați proiectul?', 'Архівувати об\'єкт?')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-gray-600">
              {txt(
                `Êtes-vous sûr de vouloir archiver "${archiveProjectName}" ?`,
                `Вы уверены, что хотите архивировать "${archiveProjectName}"?`,
                `Sigur doriți să arhivați "${archiveProjectName}"?`,
                `Ви впевнені, що хочете архівувати "${archiveProjectName}"?`
              )}
            </p>
            <p className="text-sm text-gray-500">
              {txt(
                'Le chantier sera déplacé dans les archives et pourra être restauré ultérieurement.',
                'Проект будет перемещен в архив и его можно будет восстановить позже.',
                'Proiectul va fi mutat în arhivă și poate fi restaurat mai târziu.',
                'Об\'єкт буде переміщено в архів і його можна буде відновити пізніше.'
              )}
            </p>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => {
                  setShowArchiveConfirm(false);
                  setArchiveProjectId(null);
                  setArchiveProjectName('');
                }}
                variant="outline"
                className="btn-secondary flex-1"
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={confirmArchive}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-archive-btn"
              >
                <Archive className="w-4 h-4 mr-2" />
                {language === 'ru' ? 'Архивировать' : language === 'md' ? 'Arhivează' : 'Archiver'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Daily Photos Dialog */}
      <Dialog open={showDailyPhotos} onOpenChange={setShowDailyPhotos}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2" style={{ color: 'var(--color-primary)' }}>
              <Camera className="w-5 h-5" />
              {language === 'ru' ? 'Фото работ' : language === 'md' ? 'Foto lucrări' : 'Photos du chantier'}
              {dailyPhotosProject && ` - ${dailyPhotosProject.name}`}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add photos section - Multiple upload */}
            <div className="border-2 border-dashed rounded-xl p-4">
              <label className={`flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors py-4 ${uploadingPhotos ? 'pointer-events-none opacity-50' : ''}`}>
                {uploadingPhotos ? (
                  <>
                    <div className="spinner mb-2" />
                    <span className="font-semibold" style={{ color: 'var(--color-primary)' }}>
                      {language === 'ru' ? 'Загрузка...' : language === 'md' ? 'Se încarcă...' : 'Chargement...'}
                    </span>
                  </>
                ) : (
                  <>
                    <Camera className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="font-semibold" style={{ color: 'var(--color-primary)' }}>
                      {language === 'ru' ? 'Добавить фото' : language === 'md' ? 'Adaugă foto' : 'Ajouter des photos'}
                    </span>
                    <span className="text-sm text-gray-500 text-center">
                      {language === 'ru' ? 'Выберите одну или несколько фото' : language === 'md' ? 'Selectați una sau mai multe fotografii' : 'Sélectionnez une ou plusieurs photos'}
                    </span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  disabled={uploadingPhotos}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) {
                      handleAddMultiplePhotos(files);
                      e.target.value = ''; // Reset input
                    }
                  }}
                />
              </label>
            </div>

            {/* Photos count */}
            {dailyPhotos.length > 0 && (
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                {dailyPhotos.length} {language === 'ru' ? 'фото' : language === 'md' ? 'fotografii' : 'photo(s)'}
              </div>
            )}

            {/* Photos list */}
            {loadingPhotos ? (
              <div className="flex items-center justify-center py-8">
                <div className="spinner" />
              </div>
            ) : dailyPhotos.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{language === 'ru' ? 'Нет фотографий' : language === 'md' ? 'Fără fotografii' : 'Aucune photo'}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Group by date */}
                {Object.entries(
                  dailyPhotos.reduce((acc, photo) => {
                    // Use date field or extract from created_at
                    const date = photo.date || (photo.created_at ? photo.created_at.split('T')[0] : new Date().toISOString().split('T')[0]);
                    if (!acc[date]) acc[date] = [];
                    acc[date].push(photo);
                    return acc;
                  }, {})
                ).sort(([a], [b]) => b.localeCompare(a)).map(([date, photos]) => (
                  <div key={date} className="space-y-2">
                    <h4 className="font-semibold text-sm text-gray-600 flex items-center justify-between">
                      <span>{new Date(date + 'T12:00:00').toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">{photos.length} photo{photos.length > 1 ? 's' : ''}</span>
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {photos.map(photo => (
                        <div key={photo.id} className="relative group">
                          <img
                            src={photo.photo_data}
                            alt={photo.description || 'Photo'}
                            className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity select-none"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewPhoto(photo.photo_data);
                              setShowDailyPhotos(false);
                            }}
                            draggable={false}
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 rounded-b-lg pointer-events-none">
                            <p className="text-white text-xs truncate">{photo.user_name}</p>
                            <p className="text-white/70 text-xs">
                              {new Date(photo.created_at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteDailyPhoto(photo.id); }}
                            className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Document Preview Modal */}
      {previewDoc && (
        <div 
          /* z au-dessus de la barre de navigation du bas (z-index 50 + transform,
             elle masquait le bouton Telecharger sur telephone). */
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setPreviewDoc(null)}
        >
          <button
            onClick={() => setPreviewDoc(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white z-[10000]"
          >
            <X className="w-5 h-5" />
          </button>
          <img 
            src={previewDoc} 
            alt="Document" 
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); telechargerPhoto(previewDoc, `photo-${Date.now()}.jpg`); }}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 px-6 py-3 bg-white text-black rounded-full font-semibold shadow-xl hover:bg-gray-100 transition-colors flex items-center gap-2"
            data-testid="telecharger-document"
          >
            <Download className="w-5 h-5" />
            {language === 'ru' ? 'Скачать' : language === 'md' ? 'Descarcă' : language === 'uk' ? 'Завантажити' : 'Télécharger'}
          </button>
        </div>
      )}
      
      {/* Photo Preview Modal - Using Portal to render outside Dialog */}
      {previewPhoto && createPortal(
        <div 
          id="photo-preview-modal"
          className="fixed inset-0 bg-black flex items-center justify-center p-4"
          style={{ zIndex: 99999 }}
        >
          <button
            type="button"
            onClick={closePreview}
            className="absolute top-6 right-6 w-12 h-12 bg-white rounded-full flex items-center justify-center text-black shadow-xl hover:bg-gray-100 transition-colors"
            style={{ zIndex: 100000 }}
            data-testid="photo-preview-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img 
            src={previewPhoto} 
            alt="Photo" 
            className="max-w-full max-h-[90vh] object-contain"
          />
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3"
            style={{ zIndex: 100000 }}
          >
            <button
              type="button"
              onClick={() => telechargerPhoto(previewPhoto, `photo-${Date.now()}.jpg`)}
              className="px-6 py-3 bg-white text-black rounded-full font-semibold shadow-xl hover:bg-gray-100 transition-colors flex items-center gap-2"
              data-testid="telecharger-photo"
            >
              <Download className="w-5 h-5" />
              {language === 'ru' ? 'Скачать' : language === 'md' ? 'Descarcă' : language === 'uk' ? 'Завантажити' : 'Télécharger'}
            </button>
            <button
              type="button"
              onClick={closePreview}
              className="px-6 py-3 bg-white text-black rounded-full font-semibold shadow-xl hover:bg-gray-100 transition-colors"
            >
              {language === 'ru' ? 'Закрыть' : language === 'md' ? 'Închide' : 'Fermer'}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function ProjectCard({ project, index, locale, t, language, expanded, onToggle, onOuvrirDocument, onEdit, onDelete, onShare, onAddMaterial, onStepProgress, onAddStep, onDeleteStep, onRenameStep, onEditStepDays, onContainerChange, onReorderSteps, onDailyPhotos, setPreviewDoc, onRestore, onPermanentDelete, isArchived, isManager, onCoverPhotoChange, onCoverPhotoDelete, labor, onAddStepPhoto, onDeleteStepPhoto, onToggleStandby }) {
  const [showMapOptions, setShowMapOptions] = useState(false);
  const [showDescriptif, setShowDescriptif] = useState(false);
  const ctxt = (fr, ru, md, uk) => ({ fr, ru, md, uk }[language] || fr);
  const isLate = project.target_date && project.status === 'in_progress' && (project.overall_progress || 0) < 100
    && project.target_date < new Date().toISOString().split('T')[0];
  const startDate = new Date(project.start_date).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  const endDate = project.end_date 
    ? new Date(project.end_date).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  
  const overallProgress = project.overall_progress || 0;
  const steps = project.steps || [];

  const statusLabels = {
    in_progress: { label: t('inProgress'), color: 'badge-info' },
    sav: { label: 'SAV', color: 'bg-orange-100 text-orange-700' },
    standby: { label: 'Stand-by', color: 'bg-slate-200 text-slate-700' },
    maintenance: { label: language === 'ru' ? 'Обслуживание' : language === 'md' ? 'Întreținere' : 'Entretien', color: 'bg-cyan-100 text-cyan-700' },
    archived: { label: language === 'ru' ? 'Архив' : language === 'md' ? 'Arhivat' : 'Archivé', color: 'bg-gray-100 text-gray-600' },
    completed: { label: t('completed'), color: 'badge-success' }
  };

  const currentStatus = statusLabels[project.status] || statusLabels.in_progress;
  
  const openInWaze = () => {
    const encoded = encodeURIComponent(project.client_address);
    window.open(`https://waze.com/ul?q=${encoded}&navigate=yes`, '_blank');
    setShowMapOptions(false);
  };
  
  const openInGoogleMaps = () => {
    const encoded = encodeURIComponent(project.client_address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
    setShowMapOptions(false);
  };
  
  const callClient = () => {
    if (project.client_phone) {
      window.location.href = `tel:${project.client_phone.replace(/\s/g, '')}`;
    }
  };

  return (
    <div 
      className={`card-base overflow-hidden animate-slideUp ${project.status === 'archived' || project.status === 'completed' ? 'opacity-90' : ''}`}
      style={{ animationDelay: `${index * 0.05}s` }}
      data-testid={`project-${project.id}`}
    >
      {/* Header - Clickable to expand */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-start gap-3 mb-3">
          {/* Cover Photo */}
          <div 
            className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 group"
            onClick={(e) => e.stopPropagation()}
          >
            {project.cover_photo ? (
              <>
                <img 
                  src={project.cover_photo} 
                  alt={project.name}
                  className="w-full h-full object-cover cursor-zoom-in"
                  onClick={(e) => { e.stopPropagation(); setPreviewDoc(project.cover_photo); }}
                />
              </>
            ) : (
              <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors">
                <Camera className="w-6 h-6 text-gray-400" />
                <span className="text-[10px] text-gray-400 mt-1">Photo</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onCoverPhotoChange && onCoverPhotoChange(project.id, e)}
                />
              </label>
            )}
          </div>
          
          {/* Project Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3
                className="text-lg font-bold truncate hover:underline cursor-pointer"
                style={{ color: 'var(--color-primary)' }}
                onClick={(e) => { e.stopPropagation(); setShowDescriptif(true); }}
                title={ctxt('Voir le descriptif', 'Посмотреть описание', 'Vezi descrierea', 'Переглянути опис')}
              >
                {project.name}
              </h3>
              <span className={`badge ${currentStatus.color} shrink-0`}>
                {currentStatus.label}
              </span>
              {isLate && (
                <span className="badge bg-red-100 text-red-700 shrink-0 flex items-center gap-1" data-testid="late-badge">
                  <AlertTriangle className="w-3 h-3" />
                  {ctxt('En retard', 'Опоздание', 'Întârziat', 'Запізнення')}
                </span>
              )}
              {project.company && (
                <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full shrink-0">
                  {project.company === 'Toutes' ? '🏢 LPC+EBC' : project.company === 'La Piscine Container' ? '🏊 LPC' : '🏗️ EBC'}
                </span>
              )}
              <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </div>
            {project.client_address && (
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setShowMapOptions(!showMapOptions)}
                  className="text-blue-600 hover:text-blue-800 text-xs mb-1 flex items-start gap-1 hover:underline transition-colors text-left"
                  title={language === 'ru' ? 'Открыть на карте' : language === 'md' ? 'Deschide pe hartă' : 'Ouvrir sur la carte'}
                >
                  <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
                  <span className="break-words">{project.client_address}</span>
                </button>
                {showMapOptions && (
                  <div className="absolute left-0 top-6 z-50 bg-white rounded-lg shadow-lg border p-2 min-w-[160px]">
                    <button
                      onClick={openInWaze}
                      className="w-full p-2 text-left text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                    >
                      <Navigation className="w-4 h-4 text-blue-500" />
                      Waze
                    </button>
                    <button
                      onClick={openInGoogleMaps}
                      className="w-full p-2 text-left text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                    >
                      <MapPin className="w-4 h-4 text-red-500" />
                      Google Maps
                    </button>
                  </div>
                )}
              </div>
            )}
            {project.client_phone && (
              <button
                onClick={(e) => { e.stopPropagation(); callClient(); }}
                className="text-green-600 hover:text-green-800 text-xs mb-1 flex items-center gap-1 hover:underline transition-colors"
                title={language === 'ru' ? 'Позвонить' : language === 'md' ? 'Sună' : 'Appeler'}
              >
                <Phone className="w-3 h-3" />
                <span>{project.client_phone}</span>
              </button>
            )}
            {project.description && (
              <p className="text-gray-600 text-sm line-clamp-2">{project.description}</p>
            )}
          </div>
          
          <div className="flex flex-col gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
            {/* Sketch/Croquis button */}
            {(project.sketch_photo || project.a_sketch_photo) && (
              <button
                onClick={() => onOuvrirDocument(project, 'sketch_photo')}
                className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                title={language === 'ru' ? 'Эскиз' : language === 'md' ? 'Schiță' : 'Croquis'}
              >
                <FileText className="w-4 h-4" />
              </button>
            )}
            {/* Delivery note button */}
            {(project.delivery_note_photo || project.a_delivery_note_photo) && (
              <button
                onClick={() => onOuvrirDocument(project, 'delivery_note_photo')}
                className="p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                title={language === 'ru' ? 'Накладная' : language === 'md' ? 'Bon de livrare' : 'Bon de livraison'}
              >
                <FileCheck className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onDailyPhotos}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title={language === 'ru' ? 'Фото работ' : language === 'md' ? 'Foto lucrări' : 'Photos du chantier'}
            >
              <Camera className="w-4 h-4" />
            </button>
            {!isArchived && (
              <button
                onClick={(e) => { e.stopPropagation(); onAddMaterial(); }}
                className="p-2 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
                title={language === 'ru' ? 'Добавить материал' : language === 'md' ? 'Adaugă material' : 'Ajouter un matériau'}
                data-testid={`add-material-shortcut-${project.id}`}
              >
                <Package className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onShare}
              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title={language === 'ru' ? 'Поделиться' : language === 'md' ? 'Partajează' : 'Partager'}
            >
              <Share2 className="w-4 h-4" />
            </button>
            {/* Stand-by / reprise : manager, chantiers en cours ou en pause */}
            {isManager && onToggleStandby && (project.status === 'in_progress' || project.status === 'standby') && (
              <button
                onClick={onToggleStandby}
                className={`p-2 rounded-lg transition-colors ${project.status === 'standby' ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:text-slate-700 hover:bg-slate-100'}`}
                title={project.status === 'standby'
                  ? ctxt('Reprendre le chantier', 'Возобновить', 'Reia șantierul', 'Відновити')
                  : ctxt('Mettre en stand-by', 'Поставить на паузу', 'Pune în așteptare', 'Поставити на паузу')}
                data-testid={`toggle-standby-${project.id}`}
              >
                {project.status === 'standby' ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </button>
            )}
            {/* Only manager can edit projects */}
            {isManager && (
              <button
                onClick={onEdit}
                className="p-2 text-gray-400 hover:text-[#0E2147] hover:bg-[#0E2147]/10 rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4" />
              </button>
            )}
            {/* For archived projects: show restore and permanent delete buttons */}
            {isArchived && isManager && (
              <>
                {/* Pas de restauration pour un stock permanent */}
                {!project.is_stock && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRestore();
                    }}
                    className="p-2 text-gray-400 hover:text-emerald-500 hover:bg-emerald-50 rounded-lg transition-colors"
                    data-testid={`restore-project-${project.id}`}
                    title={language === 'ru' ? 'Восстановить' : language === 'md' ? 'Restaurează' : 'Restaurer'}
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                )}
                {/* Pas de suppression pour un stock permanent */}
                {!project.is_stock && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onPermanentDelete();
                    }}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    data-testid={`delete-project-${project.id}`}
                    title={language === 'ru' ? 'Удалить навсегда' : language === 'md' ? 'Șterge permanent' : 'Supprimer définitivement'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
            {/* Chantiers actifs : bouton archiver, jamais pour un stock permanent */}
            {!isArchived && isManager && !project.is_stock && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                data-testid={`archive-project-${project.id}`}
                title={language === 'ru' ? 'Архивировать' : language === 'md' ? 'Arhivează' : 'Archiver'}
              >
                <Archive className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        
        {/* Overall Progress */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>{t('overallProgress')}</span>
            <span className={`text-lg font-bold ${overallProgress === 100 ? 'text-emerald-600' : ''}`} style={overallProgress < 100 ? { color: 'var(--color-primary)' } : {}}>
              {overallProgress}%
            </span>
          </div>
          <div className="progress-bar">
            <div 
              className={`progress-bar-fill ${overallProgress === 100 ? 'complete' : ''}`} 
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>
        
        {/* Info */}
        <div className="flex items-center justify-between text-sm flex-wrap gap-y-1">
          <div className="flex items-center gap-4 text-gray-500 flex-wrap">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{startDate}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{project.estimated_days} {t('days')}</span>
            </div>
            {project.target_date && project.status !== 'archived' && project.status !== 'completed' && (
              <div className={`flex items-center gap-1 ${isLate ? 'text-red-600 font-semibold' : ''}`}>
                <span>🚚</span>
                <span>{new Date(project.target_date + 'T12:00:00').toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              </div>
            )}
            {isManager && labor && labor.days > 0 && (
              <div className="flex items-center gap-1 text-slate-600" data-testid="labor-stats" title={ctxt('Main-d\'œuvre (jours-homme)', 'Трудозатраты (человеко-дни)', 'Manoperă (zile-om)', 'Трудовитрати (людино-дні)')}>
                <Users className="w-4 h-4" />
                <span>{labor.days % 1 === 0 ? labor.days : labor.days.toFixed(1)} {ctxt('j-h', 'ч-дн', 'zile-om', 'л-дн')}</span>
              </div>
            )}
          </div>

          {(project.status === 'archived' || project.status === 'completed') && endDate && (
            <span className="text-emerald-600 font-medium">
              {t('completedOn')} {endDate}
            </span>
          )}
        </div>
      </div>
      
      {/* Expand/Collapse Button */}
      <button
        onClick={onToggle}
        className="w-full p-3 bg-gray-50 border-t flex items-center justify-center gap-2 hover:bg-gray-100 transition-colors"
        style={{ borderColor: 'var(--color-border)', color: 'var(--color-text)' }}
      >
        <span className="text-sm font-semibold">{t('steps')} ({steps.length})</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {/* Steps with Drag & Drop */}
      {expanded && (
        <>
          <StepsContainer
            steps={steps}
            projectId={project.id}
            onStepProgress={onStepProgress}
            onDeleteStep={onDeleteStep}
            onRenameStep={onRenameStep}
            onEditStepDays={onEditStepDays}
            onReorderSteps={onReorderSteps}
            onAddStep={onAddStep}
            onAddStepPhoto={onAddStepPhoto}
            onDeleteStepPhoto={onDeleteStepPhoto}
            onViewPhoto={setPreviewDoc}
            isArchived={project.status === 'archived' || project.status === 'completed'}
            isManager={isManager}
            t={t}
          />
          <ActivityJournal activity={project.activity} language={language} locale={locale} />
        </>
      )}

      {/* Descriptif Modal (tailles + options, sans tarifs) */}
      {showDescriptif && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ zIndex: 99999, background: 'rgba(0,0,0,0.6)' }}
          onClick={() => setShowDescriptif(false)}
          data-testid={`descriptif-overlay-${project.id}`}
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
              <h3 className="text-lg font-bold" style={{ color: 'var(--color-primary)' }}>{project.name}</h3>
              <button
                onClick={() => setShowDescriptif(false)}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500"
                data-testid={`descriptif-close-${project.id}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                {ctxt('Descriptif (tailles et options)', 'Описание (размеры и опции)', 'Descriere (dimensiuni și opțiuni)', 'Опис (розміри та опції)')}
              </p>
              {project.description ? (
                <p className="text-gray-700 text-sm whitespace-pre-line leading-relaxed">{project.description}</p>
              ) : (
                <p className="text-gray-400 text-sm italic">
                  {ctxt('Aucun descriptif renseigné.', 'Описание не заполнено.', 'Nicio descriere completată.', 'Опис не заповнено.')}
                </p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function ActivityJournal({ activity, language, locale }) {
  const [open, setOpen] = useState(false);
  const jtxt = (fr, ru, md, uk) => ({ fr, ru, md, uk }[language] || fr);
  const entries = (activity || []).slice().reverse();
  if (entries.length === 0) return null;

  const renderEntry = (e) => {
    const step = translateStepName(e.params?.step || '', language);
    switch (e.key) {
      case 'step_progress':
        return `${e.user} : ${step} → ${e.params?.pct}%`;
      case 'step_added':
        return `${e.user} ${jtxt("a ajouté l'étape", 'добавил этап', 'a adăugat etapa', 'додав етап')} « ${step} »`;
      case 'step_renamed':
        return `${e.user} ${jtxt('a renommé', 'переименовал', 'a redenumit', 'перейменував')} « ${translateStepName(e.params?.old || '', language)} » → « ${translateStepName(e.params?.new || '', language)} »`;
      case 'step_deleted':
        return `${e.user} ${jtxt("a supprimé l'étape", 'удалил этап', 'a șters etapa', 'видалив етап')} « ${step} »`;
      case 'step_photo_added':
        return `${e.user} ${jtxt('a ajouté une photo à', 'добавил фото к', 'a adăugat o fotografie la', 'додав фото до')} « ${step} »`;
      case 'steps_reordered':
        return `${e.user} ${jtxt('a réorganisé les étapes', 'изменил порядок этапов', 'a reordonat etapele', 'змінив порядок етапів')}`;
      default:
        return `${e.user} : ${e.key}`;
    }
  };

  return (
    <div className="border-t" style={{ borderColor: 'var(--color-border)' }}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full p-3 bg-gray-50 flex items-center justify-center gap-2 hover:bg-gray-100 transition-colors text-gray-600"
        data-testid="activity-journal-toggle"
      >
        <History className="w-4 h-4" />
        <span className="text-sm font-semibold">{jtxt('Journal', 'Журнал', 'Jurnal', 'Журнал')} ({entries.length})</span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <div className="p-3 space-y-1.5 max-h-64 overflow-y-auto bg-white">
          {entries.map((e, i) => (
            <div key={i} className="text-xs flex items-start gap-2">
              <span className="text-gray-400 whitespace-nowrap">
                {new Date(e.ts).toLocaleDateString(locale, { day: 'numeric', month: 'short' })}{' '}
                {new Date(e.ts).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className="text-gray-700">{renderEntry(e)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StepsContainer({ steps, projectId, onStepProgress, onDeleteStep, onRenameStep, onEditStepDays, onReorderSteps, onAddStep, onAddStepPhoto, onDeleteStepPhoto, onViewPhoto, isArchived, isManager, t }) {
  const [localSteps, setLocalSteps] = useState(steps);
  
  useEffect(() => {
    setLocalSteps(steps);
  }, [steps]);
  
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const oldIndex = localSteps.findIndex(s => s.id === active.id);
    const newIndex = localSteps.findIndex(s => s.id === over.id);
    const newSteps = arrayMove(localSteps, oldIndex, newIndex);
    
    setLocalSteps(newSteps);
    onReorderSteps(projectId, newSteps);
  };
  
  return (
    <div className="p-4 bg-gray-50 border-t space-y-3" style={{ borderColor: 'var(--color-border)' }}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={localSteps.map(s => s.id)} strategy={verticalListSortingStrategy}>
          {localSteps.map((step, stepIndex) => (
            <SortableStepItem
              key={step.id}
              step={step}
              index={stepIndex}
              projectId={projectId}
              onProgressChange={onStepProgress}
              onDelete={onDeleteStep}
              onRename={onRenameStep}
              onEditDays={onEditStepDays}
              onAddPhoto={onAddStepPhoto}
              onDeletePhoto={onDeleteStepPhoto}
              onViewPhoto={onViewPhoto}
              disabled={isArchived}
              isManager={isManager}
            />
          ))}
        </SortableContext>
      </DndContext>
      
      {/* Tout le monde peut ajouter une étape (ordre Pavel 2026-08-25) : le
          manager reçoit une notification à chaque ajout. */}
      {!isArchived && (
        <button
          onClick={() => onAddStep(projectId)}
          className="w-full p-3 border-2 border-dashed rounded-xl text-gray-500 hover:border-[#0E2147] hover:text-[#0E2147] hover:bg-[#0E2147]/5 transition-colors flex items-center justify-center gap-2"
          data-testid="add-step-btn"
        >
          <Plus className="w-4 h-4" />
          {t('addStep')}
        </button>
      )}
    </div>
  );
}

function SortableStepItem({ step, index, projectId, onProgressChange, onDelete, onRename, onEditDays, onAddPhoto, onDeletePhoto, onViewPhoto, disabled, isManager }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  const { language } = useLanguage();
  const stxt = (fr, ru, md, uk) => ({ fr, ru, md, uk }[language] || fr);
  const isComplete = step.progress === 100;
  const stepPhotos = step.photos || [];
  // Galerie coulissante des photos de l'étape (Pavel 25/09/2026) : on ouvre à
  // l'index cliqué et on navigue flèches + glissement, sans fermer/rouvrir.
  const [galleryIdx, setGalleryIdx] = useState(null);
  const touchX = useRef(null);
  const galleryOpen = galleryIdx != null && stepPhotos[galleryIdx];
  const galleryPrev = () => setGalleryIdx((i) => (i > 0 ? i - 1 : stepPhotos.length - 1));
  const galleryNext = () => setGalleryIdx((i) => (i < stepPhotos.length - 1 ? i + 1 : 0));
  useEffect(() => {
    if (galleryIdx == null) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') galleryPrev();
      else if (e.key === 'ArrowRight') galleryNext();
      else if (e.key === 'Escape') setGalleryIdx(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [galleryIdx, stepPhotos.length]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };
  
  return (
    <div 
      ref={setNodeRef}
      style={{ ...style, borderColor: !isComplete ? 'var(--color-border)' : undefined }}
      className={`p-4 rounded-xl border-2 transition-colors ${
        isComplete 
          ? 'bg-emerald-50 border-emerald-300' 
          : 'bg-white'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {!disabled && (
            <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600">
              <GripVertical className="w-5 h-5" />
            </button>
          )}
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
            isComplete ? 'bg-emerald-500 text-white' : 'text-white'
          }`} style={!isComplete ? { backgroundColor: 'var(--color-primary)' } : {}}>
            {isComplete ? <CheckCircle2 className="w-5 h-5" /> : index + 1}
          </div>
          <span className={`font-semibold ${isComplete ? 'text-emerald-700' : ''}`} style={!isComplete ? { color: 'var(--color-text)' } : {}}>
            {translateStepName(step.name, language)}
            {(step.days || 0) > 0 && (
              <span className="ml-2 text-xs font-medium text-gray-500 whitespace-nowrap">
                · {String(step.days).replace('.', ',')} {stxt('j', 'дн', 'z', 'дн')}
              </span>
            )}
          </span>
        </div>
        {/* Renommer : ouvert à tous (ordre Pavel 2026-08-25), le manager est
            notifié. Supprimer : manager uniquement, geste destructif. */}
        {!disabled && (
          <div className="flex gap-1">
            <button
              onClick={() => onRename(projectId, step.id, step.name)}
              className="p-1 text-gray-400 hover:text-[#0E2147] rounded"
              title="Renommer"
            >
              <Pencil className="w-4 h-4" />
            </button>
            {isManager && onEditDays && (
              <button
                onClick={() => onEditDays(projectId, step)}
                className="p-1 text-gray-400 hover:text-[#0E2147] rounded"
                title="Modifier la durée (jours)"
              >
                <Clock className="w-4 h-4" />
              </button>
            )}
            {isManager && (
              <button
                onClick={() => onDelete(projectId, step.id)}
                className="p-1 text-gray-400 hover:text-red-500 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Avancement de l'étape, VISIBLE (Pavel 25/09/2026) : barre + % + jours faits/total.
          Le manager et l'équipe voient d'un coup d'oeil où en est chaque étape. */}
      {(() => {
        const pct = step.progress || 0;
        const nb = Math.ceil(step.days || 0);
        const faits = nb > 0 ? Math.round(pct / 100 * nb) : 0;
        return (
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-medium" style={{ color: pct === 100 ? '#059669' : 'var(--color-text)' }}>
                {stxt('Avancement', 'Прогресс', 'Progres', 'Прогрес')} : {pct} %
              </span>
              {nb > 0 && (
                <span className="text-gray-500">{faits}/{nb} {stxt('j faits', 'дн сделано', 'z făcute', 'дн зроблено')}</span>
              )}
            </div>
            <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
              <div className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, backgroundColor: pct === 100 ? '#10b981' : 'var(--color-primary)' }} />
            </div>
          </div>
        );
      })()}

      {/* Progress Selector : découpé par JOUR quand l'étape a une durée (Pavel 17/09/2026).
          1 j = 100 % d'un coup ; 2 j = J1 50 % puis J2 100 % ; N j = J1..JN. Un bouton est
          rempli dès que l'avancement l'atteint (lecture cumulative). La valeur envoyée reste
          un pourcentage, donc le calcul d'avancement global ne change pas. Sans durée : paliers
          classiques 0/25/50/75/100. */}
      {(() => {
        const nd = step.days || 0;
        const nb = Math.ceil(nd);  // demi-journee (1,5) -> 2 boutons de jour, arrondi sup.
        const options = nd > 0
          ? [{ v: 0, label: '0' },
             ...Array.from({ length: nb }, (_, i) => ({
               v: i + 1 === nb ? 100 : Math.round((i + 1) * 100 / nb),
               label: `J${i + 1}`,
             }))]
          : PROGRESS_OPTIONS.map(v => ({ v, label: `${v}%` }));
        return (
          <div className="flex flex-wrap gap-2">
            {options.map(({ v, label }) => {
              // Lecture cumulative : le "0" reste foncé une fois l'étape lancée
              // (Pavel 25/09/2026 : à J1, le 0 ET le J1 sont foncés).
              const active = step.progress >= v;
              const exact = step.progress === v;
              return (
                <button
                  key={v + '-' + label}
                  onClick={() => !disabled && onProgressChange(projectId, step.id, v)}
                  disabled={disabled}
                  title={nd > 0 && v > 0 ? `${label} · ${v}%` : undefined}
                  className={`flex-1 min-w-[46px] py-2 px-1 text-sm font-semibold rounded-lg transition-all ${
                    active
                      ? v === 100
                        ? 'bg-emerald-500 text-white' + (exact ? ' shadow-md scale-105' : '')
                        : 'bg-[#0E2147] text-white' + (exact ? ' shadow-md scale-105' : '')
                      : disabled
                        ? 'bg-gray-100 text-gray-400'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`progress-btn-${v}`}
                >
                  {label}
                  {nd > 0 && v > 0 && <span className="hidden sm:inline text-[10px] font-normal opacity-80"> · {v}%</span>}
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* Step photos */}
      {(stepPhotos.length > 0 || !disabled) && (
        <div className="flex gap-2 flex-wrap mt-3 items-center">
          {stepPhotos.map((photo, pIdx) => (
            <div key={pIdx} className="relative">
              <img
                src={photo}
                alt=""
                className="w-12 h-12 rounded-lg object-cover border cursor-pointer"
                onClick={() => setGalleryIdx(pIdx)}
                data-testid={`step-photo-${step.id}-${pIdx}`}
              />
              {isManager && !disabled && (
                <button
                  onClick={() => onDeletePhoto && onDeletePhoto(projectId, step.id, pIdx)}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              )}
            </div>
          ))}
          {!disabled && onAddPhoto && (
            <label
              className="w-12 h-12 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer text-gray-400 hover:border-gray-400 hover:text-gray-500 transition-colors"
              title={stxt('Ajouter une photo de cette étape', 'Добавить фото этого этапа', 'Adaugă o fotografie a acestei etape', 'Додати фото цього етапу')}
              data-testid={`add-step-photo-${step.id}`}
            >
              <Camera className="w-4 h-4" />
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onAddPhoto(projectId, step.id, e)}
              />
            </label>
          )}
        </div>
      )}

      {galleryOpen && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4 select-none"
          onClick={() => setGalleryIdx(null)}
          onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (touchX.current == null) return;
            const dx = e.changedTouches[0].clientX - touchX.current;
            if (dx > 50) galleryPrev();
            else if (dx < -50) galleryNext();
            touchX.current = null;
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setGalleryIdx(null); }}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white z-[10000]"
          >
            <X className="w-5 h-5" />
          </button>

          {stepPhotos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); galleryPrev(); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white z-[10000]"
                aria-label="Photo précédente"
              >
                <ChevronDown className="w-6 h-6 rotate-90" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); galleryNext(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white z-[10000]"
                aria-label="Photo suivante"
              >
                <ChevronDown className="w-6 h-6 -rotate-90" />
              </button>
            </>
          )}

          <img
            src={stepPhotos[galleryIdx]}
            alt=""
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />

          {stepPhotos.length > 1 && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/20 text-white text-sm font-medium">
              {galleryIdx + 1} / {stepPhotos.length}
            </div>
          )}

          <a
            href={stepPhotos[galleryIdx]}
            download={`photo-${step.id}-${galleryIdx + 1}.jpg`}
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 px-6 py-3 bg-white text-black rounded-full font-semibold shadow-xl hover:bg-gray-100 transition-colors flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            {stxt('Télécharger', 'Скачать', 'Descarcă', 'Завантажити')}
          </a>
        </div>,
        document.body
      )}
    </div>
  );
}
